import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LOGO_PNG_BASE64, LOGO_ASPECT, ITALIANA_TTF_BASE64 } from "./pdfAssets"; // logo + Italiana font 
// for the PDF header

/* ============================================================
   Dwellora Designs – Furniture & Requirement Planner (React)
   Needs:  npm install jspdf jspdf-autotable
   ============================================================ */

/* ===== Settings – change these for your website ===== */
const WA_NUMBER = "919158914222"; // business WhatsApp number with country code (no + or spaces)

// production url
// const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// local host check
const API_BASE_URL = (
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_ADMIN_API_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "http://localhost:3005"
    : process.env.REACT_APP_API_BASE_URL || ""
).replace(/\/$/, "");

const SUBMIT_URL = `${API_BASE_URL}/api/furniture-enquiry`;

/* ===== PDF settings ===== */
const PDF_PURPLE = [30, 41, 59]; // [4, 131, 145];
const PDF_DARK = [30, 41, 59];
const PDF_GREY = [110, 110, 110];
const PDF_PAGE_BG = [244, 241, 234]; // #f4f1ea
const PDF_WHITE = [255, 255, 255];
const PDF_ORANGE = [210, 112, 58]; // #d2703a

const PAYMENT_MILESTONES = [
  ["Advance against PO", 50],
  ["On 50% completion of work", 25],
  ["On 75% completion of work", 20],
  ["After completion of work", 5],
];
const TERMS = [
  "Measurement as actual.",
  "This is an indicative estimate (±10%). Final price is confirmed after the designer reviews the requirement and site measurements.",
  "Architectural consultancy fees include 3D views, material selection and 5 site visits (8% on total cost of project).",
  "Civil work quantities are confirmed after a site visit; final billing is based on actual site measurements.",
  "Prices are valid for 30 days from the date of this quotation.",
  "The above quote excludes toilet, bath and kitchen fixtures and hardware.",
  "The above quote is excluding GST.",
];

/* ===== PRICING (INR) – rates taken from the reference quotation (Choudhari Sir) ===== */
const GST = 0; // reference quotation is "excluding GST" – set 0.18 to add GST back
const GST_LABEL = GST ? `${GST * 100}%` : "excluded";
const INSTALL_PCT = 0.08; // architectural consultancy fees – 8% on total project cost (3D views, material selection, 5 site visits)
const DISCOUNT_ROUND = 5000; // reference quote rounds the total down to a round figure (discount = remainder). Set 0 to switch off.

const WARDROBE_RATE = {
  "Sliding – with glass": 1800,
  "Sliding – without glass": 1600,
  "Sliding – mirror": 1700,
  "Openable (hinged)": 1600,
  "Openable – fluted glass": 1800,
  "Walk-in wardrobe": 1800,
};
const KITCHEN_RATE = { Laminate: 9000, Acrylic: 12500, "PU / Lacquer": 16000 }; // per running ft
const ISLAND_EXTRA = 35000;

/* Bed – priced per sq ft of bed area (reference: king 6'0" x 6'6" hydraulic storage = 40 sq ft x 1600) */
const BED_RATE = {
  Standard: 1000,
  "Storage bed": 1300,
  "Hydraulic storage": 1600,
  Upholstered: 1400,
};
const BED_CATALOGUE = {};
/* Bed back – [rate per sq ft, height in ft]; width = bed width (reference: 24 sq ft x 1200) */
const BEDBACK = {
  "Simple wooden": [1000, 4],
  "Upholstered panel": [1200, 4],
  "Full wall panelling": [1200, 8],
  "Cane / fluted": [1500, 4],
};
const BEDSIDE_SQFT = 4; // per table (reference: 2 tables = 8 sq ft x 1600)
const BEDSIDE_RATE = 1600;
/* TV unit – [length, width, rate per sq ft] */
const TVU = { "Wall-mounted": [5, 3, 1600], "Floor standing": [6, 3, 1600] };
/* Pooja – [length, width] ft by style; rate 2000/sq ft (reference: 70 sq ft x 2000) */
const POOJA_DIM = {
  "Wall-mounted": [5, 2.5],
  "Floor standing": [10, 7],
  "In-wall niche": [5, 3],
};
const POOJA_RATE = 2000;
const POOJA_TIER = { Premium: 1, Luxury: 2.2 };

const P = {
  "Sofa|3-seater": 28000,
  "Sofa|3+2 set": 55000,
  "Sofa|L-shape sectional": 65000,
  "Sofa|3+2+1 set": 75000,
  "Dining table|4-seater with chairs": 35000,
  "Dining table|6-seater with chairs": 55000,
  "Dining table|8-seater with chairs": 80000,
  "Safety door|Standard": 60000,
  "Safety door|Premium": 85000,
  "Safety door|Luxury": 130000,
};

/* ===== PACKAGES (Basic / Premium / Luxury) =====
   mult  = multiplier applied to every furniture / carpentry rate (civil work and electrical are NOT multiplied).
           Premium = 1, so Premium gives exactly the rates saved in the Rate Master.
   specs = [category, specification] rows – shown on the package cards (first 5) and printed in full in the quotation PDF.
   NOTE: the PDF font cannot print the rupee sign, so specs use "Rs".
   EDIT the Basic and Luxury multipliers / specification lines below as per your actual materials. */
const PACKAGES = {
  basic: {
    name: "Basic",
    mult: 0.85,
    specs: [
      ["Plywood (carcass & shutters)", "Commercial grade ply with standard face"],
      ["Outer laminate", "Laminate up to Rs 1,500/sheet"],
      ["Internal laminate", "Standard internal laminate, MRP Rs 450/sheet"],
      ["Hardware & fittings", "Standard branded fittings"],
      ["Upholstery (bed / sofa)", "Standard foam and fabric up to Rs 250/mtr"],
      ["Hydraulic storage bed", "Standard hydraulic pump"],
      ["Kitchen accessories", "BWR ply with standard fittings, standard tandem boxes, laminate trolley shutters"],
      ["Pooja unit", "MDF back sheet with basic paint, wooden jali as per design"],
      ["Safety door", "Metal jali / PU colour, pine wood, without lock and handles"],
    ],
  },
  premium: {
    name: "Premium",
    mult: 1,
    specs: [
      ["Plywood (carcass & shutters)", "Redcore gurjan face ply"],
      ["Outer laminate", "Marino Laminate up to Rs 2,000/sheet"],
      ["Internal laminate", "Standard fabric texture internal laminate, MRP Rs 600/sheet"],
      ["Hardware & fittings", "Hettich fittings"],
      ["Upholstery (bed / sofa)", "Heavy foam and good quality fabric up to Rs 400/mtr"],
      ["Hydraulic storage bed", "Ebco hydraulic pump"],
      ["Kitchen accessories", "BWP waterproof ply with Hettich fitting, tandem boxes with alternate waterproof ply base, back & front and internal white laminate, acrylic trolley shutters with acrylic sheet up to MRP Rs 4,000/sheet"],
      ["Pooja unit", "MDF back sheet with PU paint, wooden / metal jali as per design"],
      ["Safety door", "Metal jali / PU colour, 100% pine wood, without lock and handles"],
    ],
  },
  luxury: {
    name: "Luxury",
    mult: 1.3,
    specs: [
      ["Plywood (carcass & shutters)", "Premium BWP ply with veneer / designer face"],
      ["Outer laminate", "Laminate / acrylic up to Rs 3,500/sheet"],
      ["Internal laminate", "Premium internal laminate, MRP Rs 900/sheet"],
      ["Hardware & fittings", "Hettich / Hafele premium fittings with soft-close"],
      ["Upholstery (bed / sofa)", "High-density foam and premium fabric up to Rs 800/mtr"],
      ["Hydraulic storage bed", "Ebco hydraulic pump with heavy-duty gas struts"],
      ["Kitchen accessories", "BWP waterproof ply with premium soft-close fittings, premium tandem boxes with organisers, acrylic / PU trolley shutters with acrylic sheet up to MRP Rs 6,000/sheet"],
      ["Pooja unit", "MDF back sheet with PU paint, carved wooden / brass jali as per design"],
      ["Safety door", "Designer metal jali / PU colour, premium wood finish, without lock and handles"],
    ],
  },
};
/* Rate shown on each package card: sliding wardrobe (without glass) per sq ft, after the package multiplier */
const pkgSqftRate = (key) =>
  Math.round(WARDROBE_RATE["Sliding – without glass"] * PACKAGES[key].mult);

/* Material specifications stored with each item (no longer printed in the PDF – see PACKAGES) */
const PLY =
  "Redcore gurjan face ply, Marino Laminate up to Rs 2000/sheet (standard fabric texture internal laminate MRP Rs 600/sheet)";
const FOAM = "Heavy foam and good quality fabric up to Rs 400/mtr";
const EX_SPEC = {
  "Tandem box set (9 nos)":
    "750mm x 150mm x 520mm tandem box with base, back & front of alternate waterproof ply, internal white laminate, external laminate (standard fabric texture internal laminate MRP Rs 600/sheet)",
  "Acrylic trolley shutters":
    "Waterproof ply with gurjan face full core full panel, acrylic sheet up to MRP Rs 4000/sheet (standard fabric texture internal laminate MRP Rs 600/sheet)",
  "Storage below sink":
    "BWP waterproof ply, Marino Laminate up to Rs 2000/sheet, Hettich fitting (standard fabric texture internal laminate MRP Rs 600/sheet)",
  "Decorative charcoal panels": "6\" x 8' panels (approx.)",
};

/* Extras – SQ.FT items: ["SQ.FT", length, width, rate]; others: [unit, quantity, rate] */
const EX = {
  "Dressing unit": ["SQ.FT", 5, 3, 2000],
  "Wall mirror fitting": ["SQ.FT", 8, 5, 850],
  "TV back panelling": ["SQ.FT", 8, 3, 1200],
  "TV back panelling (full wall)": ["SQ.FT", 10, 8, 1200],
  "Study table": ["SQ.FT", 5, 2, 1500],
  "TV unit": ["SQ.FT", 5, 3, 1600],
  "Bench / ottoman": ["NO", 1, 8000],
  "Loft storage": ["SQ.FT", 6, 2, 1500],
  Bookshelf: ["SQ.FT", 5, 2, 1400],
  "Bunk bed": ["NO", 1, 45000],
  "Toy storage": ["SQ.FT", 4, 2, 1500],
  "Tall unit": ["SQ.FT", 8, 5, 1600],
  "Pantry unit": ["SQ.FT", 5, 4, 1500],
  "Loft cabinets": ["SQ.FT", 10, 3, 1600],
  "Tandem box set (9 nos)": ["NO", 9, 8500],
  "Acrylic trolley shutters": ["SQ.FT", 9, 5, 1000],
  "G profile handles (5 nos)": ["NO", 5, 2800],
  "Storage below sink": ["SQ.FT", 4, 2, 1800],
  "Chimney & hob provision": ["NO", 1, 6000],
  "Breakfast counter": ["SQ.FT", 5.5, 2, 2000],
  "Utility area": ["SQ.FT", 5, 2, 2000],
  "Centre table": ["NO", 1, 9000],
  "Console table": ["NO", 1, 12000],
  "Display unit": ["SQ.FT", 5, 3, 1600],
  "Wall panelling": ["SQ.FT", 10, 3, 1500],
  "Decorative charcoal panels": ["NO", 10, 1600],
  "Crockery unit": ["SQ.FT", 5, 4, 1600],
  "Bar unit": ["SQ.FT", 6, 4, 1600],
  "Bench seating": ["NO", 1, 15000],
  "Wash basin unit": ["NO", 1, 25000],
  "Jaali doors": ["NO", 1, 8000],
  "Storage drawers": ["NO", 1, 6000],
  "LED lighting": ["NO", 1, 3500],
  "Marble / stone base": ["NO", 1, 12000],
  "Bell & brass accents": ["NO", 1, 9000],
  "Shoe rack": ["SQ.FT", 6, 4, 1600],
  "Door wall laminate panelling": ["SQ.FT", 12, 10, 1000],
  "Foyer bench": ["NO", 1, 15000],
  "Key & console unit": ["NO", 1, 12000],
  "Mirror panel": ["NO", 1, 9000],
};

/* ===== CIVIL WORK (INR) ===== */
const CW = {
  "Vitrified tile flooring": { u: "area", r: 95 },
  "Marble flooring": { u: "area", r: 260 },
  "Wooden / vinyl flooring": { u: "area", r: 140 },
  "POP false ceiling": { u: "area", r: 80 },
  "PVC sheets ceiling": { u: "area", r: 150 },
  "Plaster & putty": { u: "wall", r: 38 },
  "Luster paint": { u: "wall", r: 30 },
  Waterproofing: { u: "area", r: 60 },
  "Kitchen dado tiling": { u: "dado", r: 120 },
  "Granite kitchen platform": { u: "run", r: 1800 },
  "Plumbing modification": { u: "fixed", r: 15000 },
  "Wall demolition": { u: "fixed", r: 12000 },
  "New brick partition wall": { u: "fixed", r: 25000 },
  "Door / window frame work": { u: "fixed", r: 9000 },
  "Wall niche cutting": { u: "fixed", r: 8000 },
};
const CW_UNIT = {
  area: "SQ.FT",
  wall: "SQ.FT",
  dado: "SQ.FT",
  run: "RFT",
  fixed: "NO",
};
const FLOORING = [
  "Vitrified tile flooring",
  "Marble flooring",
  "Wooden / vinyl flooring",
];
const CW_COMMON = [
  ...FLOORING,
  "POP false ceiling",
  "Plaster & putty",
  "Luster paint",
  "Wall demolition",
  "New brick partition wall",
  "Door / window frame work",
];
const CW_LIST = {
  master: CW_COMMON,
  child: CW_COMMON,
  guest: CW_COMMON,
  living: CW_COMMON,
  dining: CW_COMMON,
  kitchen: [
    "Vitrified tile flooring",
    "Granite kitchen platform",
    "Kitchen dado tiling",
    "Waterproofing",
    "Plumbing modification",
    "Plaster & putty",
    "Luster paint",
    "Wall demolition",
  ],
  pooja: [
    "Marble flooring",
    "Wall niche cutting",
    "Plaster & putty",
    "Luster paint",
  ],
  entrance: [
    "Vitrified tile flooring",
    "Marble flooring",
    "Door / window frame work",
    "Plaster & putty",
    "Luster paint",
  ],
  terrace: [
    "PVC sheets ceiling",
    "Vitrified tile flooring",
    "Wooden / vinyl flooring",
    "Waterproofing",
    "Luster paint",
  ],
  electrical: [],
};

/* ===== ELECTRICAL WORK (whole house) – from reference quotation =====
   s = supply rate, i = install rate, d = typical quantity in the reference quote */
const ELECTRICAL = [
  {
    id: "w10",
    l: "Wiring with 3 nos 1.0 sq.mm PVC insulated Cu conductor (Polycab / RR Kabel), ISI medium gauge PVC conduit / casing capping & accessories – AC / Raw / Lighting",
    u: "MTR",
    s: 15,
    i: 10,
    d: 3000,
  },
  {
    id: "w15",
    l: "Wiring with 3 nos 1.5 sq.mm PVC insulated Cu conductor (Polycab / RR Kabel), ISI medium gauge PVC conduit / casing capping & accessories – AC / Raw / Lighting",
    u: "MTR",
    s: 20,
    i: 15,
    d: 3000,
  },
  {
    id: "w25",
    l: "Wiring with 3 nos 2.5 sq.mm PVC insulated Cu conductor (Polycab / RR Kabel), ISI medium gauge PVC conduit / casing capping & accessories – AC / Raw / Lighting",
    u: "MTR",
    s: 30,
    i: 25,
    d: 1200,
  },
  {
    id: "w40",
    l: "Same as above, wiring with 2 wires 4 sq.mm & 1 wire 4 sq.mm for power points",
    u: "MTR",
    s: 40,
    i: 40,
    d: 180,
  },
  {
    id: "lp1",
    l: "Primary light point – wiring from 5A switch on lighting switch board to first light point (2 wire 1.5 sq.mm & 1 wire 1.0 sq.mm) incl. one 6A switch, PVC conduit / casing capping & accessories",
    u: "EACH",
    s: 600,
    i: 250,
    d: 60,
  },
  {
    id: "lp2",
    l: "Secondary light point as above, without switch",
    u: "EACH",
    s: 350,
    i: 200,
    d: 40,
  },
  {
    id: "sw5",
    l: "5 AMP switch and socket",
    u: "EACH",
    s: 1000,
    i: 250,
    d: 20,
  },
  {
    id: "sw15",
    l: "15 AMP switch and socket",
    u: "EACH",
    s: 1500,
    i: 500,
    d: 10,
  },
];

const PLANNER_SETTINGS = {
  gst: GST,
  installationPct: INSTALL_PCT,
  discountRound: DISCOUNT_ROUND,
};
const PLANNER_RATE_OVERRIDES = {
  islandExtra: ISLAND_EXTRA,
  bedsideRate: BEDSIDE_RATE,
  pooja: {},
};
const PLANNER_OPTION_RATES = {};
const plannerGstLabel = () =>
  PLANNER_SETTINGS.gst ? `${PLANNER_SETTINGS.gst * 100}%` : "excluded";

/* ----- FIX (Bug 2 & 3): smarter key matching between SQL planner_keys and code option names ----- */
const slugKey = (v) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

/* SQL key -> code option name, jahan naam seedha match nahi hota */
const KEY_ALIAS = {
  slidingglass: "slidingwithglass",
  slidingnoglass: "slidingwithoutglass",
  slidingplain: "slidingwithoutglass",
  openable: "openablehinged",
  openablefluted: "openableflutedglass",
  flutedglass: "openableflutedglass",
  walkin: "walkinwardrobe",
};

/* 1) exact  2) alias  3) prefix – sirf tab jab ek hi option match kare
   (4-seater -> "4-seater with chairs", tandem-box-set -> "Tandem box set (9 nos)") */
const matchingKey = (object, value) => {
  const keys = Object.keys(object);
  const s = slugKey(value);
  if (!s) return undefined;
  const exact = keys.find((k) => slugKey(k) === s);
  if (exact) return exact;
  if (KEY_ALIAS[s]) {
    const a = keys.find((k) => slugKey(k) === KEY_ALIAS[s]);
    if (a) return a;
  }
  const pref = keys.filter((k) => slugKey(k).startsWith(s));
  return pref.length === 1 ? pref[0] : undefined;
};

/* ----- FIX (Bug 4): SQ.FT extras keep the rate at index 3, all others (NO etc.) at index 2 ----- */
const setExRate = (entry, rate) => {
  if (entry[0] === "SQ.FT") entry[3] = rate;
  else entry[2] = rate;
};

/* ----- FIX (Bug 1): built-in options (sofa, TV unit, dining, bed ...) are priced by their own
   dedicated code. Only catalogue "product:" options go through the generic pricing path. ----- */
const hasCustomOption = (v) =>
  !!PLANNER_OPTION_RATES[v] && !PLANNER_OPTION_RATES[v]._builtin;

/* Planner rate master overrides. Existing hardcoded values remain safe fallbacks
   while the admin config is being populated. */
function applyPlannerRates(products) {
  products.forEach((product) => {
    const key = String(product.planner_key || "")
      .trim()
      .toLowerCase();
    const supply = Number(product.price),
      install = Number(product.installation_price || 0),
      quantity = Number(product.default_quantity);
    if (!key || !Number.isFinite(supply)) return;
    const rate = supply + (Number.isFinite(install) ? install : 0);
    if (key === "settings:gst") PLANNER_SETTINGS.gst = supply / 100;
    else if (key === "settings:installation_pct")
      PLANNER_SETTINGS.installationPct = supply / 100;
    else if (key === "settings:discount_round")
      PLANNER_SETTINGS.discountRound = supply;
    else if (key === "island") PLANNER_RATE_OVERRIDES.islandExtra = rate;
    else if (key === "bedside") PLANNER_RATE_OVERRIDES.bedsideRate = rate;
    else if (key.startsWith("civil:")) {
      const match = matchingKey(CW, key.slice(6));
      if (match) CW[match].r = rate;
    } else if (key.startsWith("extra:")) {
      const match = matchingKey(EX, key.slice(6));
      if (match) setExRate(EX[match], rate);
    } else if (key.startsWith("bedback:")) {
      const match = matchingKey(BEDBACK, key.slice(8));
      if (match) BEDBACK[match][0] = rate;
    } else if (key.startsWith("wardrobe:")) {
      const match = matchingKey(WARDROBE_RATE, key.slice(9));
      if (match) WARDROBE_RATE[match] = rate;
    } else if (key.startsWith("kitchen:")) {
      const match = matchingKey(KITCHEN_RATE, key.slice(8));
      if (match) KITCHEN_RATE[match] = rate;
    } else if (key.startsWith("bed:")) {
      const match = matchingKey(BED_RATE, key.slice(4));
      if (match) BED_RATE[match] = rate;
    } else if (key.startsWith("tv:")) {
      const match = matchingKey(TVU, key.slice(3));
      if (match) TVU[match][2] = rate;
    } else if (key.startsWith("pooja:"))
      PLANNER_RATE_OVERRIDES.pooja[key.slice(6)] = rate;
    else if (key.startsWith("sofa:")) {
      const match = matchingKey(P, `Sofa|${key.slice(5)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("dining:")) {
      const match = matchingKey(P, `Dining table|${key.slice(7)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("safety-door:")) {
      const match = matchingKey(P, `Safety door|${key.slice(12)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("electrical:")) {
      const item = ELECTRICAL.find((entry) => entry.id === key.slice(11));
      if (item) {
        item.s = supply;
        item.i = Number.isFinite(install) ? install : 0;
        if (Number.isFinite(quantity)) item.d = quantity;
      }
    }
  });
}

function applyPlannerConfig(config) {
  const rates = Array.isArray(config?.rates) ? config.rates : [];
  Object.keys(PLANNER_OPTION_RATES).forEach(
    (key) => delete PLANNER_OPTION_RATES[key],
  );
  rates.forEach((item) => {
    const key = String(item.planner_key || "").toLowerCase(),
      base = Number(item.base_rate),
      install = Number(item.installation_rate || 0),
      quantity = Number(item.default_quantity);
    if (!key || !Number.isFinite(base)) return;
    const rate = base + (Number.isFinite(install) ? install : 0);
    const find = matchingKey;
    if (key === "setting:gst-percent" || key === "settings:gst")
      PLANNER_SETTINGS.gst = base / 100;
    else if (
      key === "setting:installation-percent" ||
      key === "settings:installation_pct"
    )
      PLANNER_SETTINGS.installationPct = base / 100;
    else if (
      key === "setting:discount-round" ||
      key === "settings:discount_round"
    )
      PLANNER_SETTINGS.discountRound = base;
    else if (key === "kitchen:island-unit")
      PLANNER_RATE_OVERRIDES.islandExtra = rate;
    else if (key === "bedside:standard")
      PLANNER_RATE_OVERRIDES.bedsideRate = rate;
    else if (key.startsWith("civil:")) {
      const match = find(CW, key.slice(6));
      if (match) CW[match].r = rate;
    } else if (key.startsWith("extra:")) {
      const match = find(EX, key.slice(6));
      if (match) setExRate(EX[match], rate);
    } else if (key.startsWith("bedback:")) {
      const match = find(BEDBACK, key.slice(8));
      if (match) BEDBACK[match][0] = rate;
    } else if (key.startsWith("wardrobe:")) {
      const match = find(WARDROBE_RATE, key.slice(9));
      if (match) WARDROBE_RATE[match] = rate;
    } else if (key.startsWith("kitchen:")) {
      const match = find(KITCHEN_RATE, key.slice(8));
      if (match) KITCHEN_RATE[match] = rate;
    } else if (key.startsWith("bed:")) {
      const match = find(BED_RATE, key.slice(4));
      if (match) BED_RATE[match] = rate;
    } else if (key.startsWith("tv-unit:")) {
      const match = find(TVU, key.slice(8));
      if (match) TVU[match][2] = rate;
    } else if (key.startsWith("pooja:"))
      PLANNER_RATE_OVERRIDES.pooja[key.slice(6)] = rate;
    else if (key.startsWith("sofa:")) {
      const match = find(P, `Sofa|${key.slice(5)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("dining:")) {
      const match = find(P, `Dining table|${key.slice(7)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("safety-door:")) {
      const match = find(P, `Safety door|${key.slice(12)}`);
      if (match) P[match] = rate;
    } else if (key.startsWith("electrical:")) {
      const electrical = ELECTRICAL.find((entry) => entry.id === key.slice(11));
      if (electrical) {
        electrical.s = base;
        electrical.i = Number.isFinite(install) ? install : 0;
        if (Number.isFinite(quantity)) electrical.d = quantity;
      }
    }
    if (key.startsWith("product:")) PLANNER_OPTION_RATES[key] = item;
  });
  const byId = new Map(rates.map((item) => [String(item.id), item]));
  const labelFor = (item) => {
    const key = String(item?.planner_key || "");
    const map = {
      "bed:standard": "Standard",
      "bed:storage": "Storage bed",
      "bed:hydraulic": "Hydraulic storage",
      "bed:upholstered": "Upholstered",
      "sofa:3-seater": "3-seater",
      "sofa:3-2-set": "3+2 set",
      "sofa:l-shape-sectional": "L-shape sectional",
      "sofa:3-2-1-set": "3+2+1 set",
      "tv-unit:wall-mounted": "Wall-mounted",
      "tv-unit:floor-standing": "Floor standing",
    };
    return map[key] || item?.display_name;
  };
  const bindings = Array.isArray(config?.bindings) ? config.bindings : [];
  bindings.forEach((binding) => {
    const item = byId.get(String(binding.planner_rate_item_id));
    const room = TYPES[binding.room_type];
    const isCatalogueOption = String(item?.planner_key || "").startsWith(
      "product:",
    );
    const label = isCatalogueOption
      ? binding.display_label || item?.display_name
      : labelFor(item);
    if (!item || !room || !label) return;
    const optionValue = isCatalogueOption ? binding.option_value : label;
    if (isCatalogueOption) {
      PLANNER_OPTION_RATES[optionValue] = { ...item, display_name: label };
      if (binding.question_key === "Civil work") {
        CW_LIST[binding.room_type] = [
          ...new Set([...(CW_LIST[binding.room_type] || []), optionValue]),
        ];
      } else if (binding.question_key === "Add more") {
        room.ex = [...new Set([...(room.ex || []), optionValue])];
      } else {
        const group = room.g.find(
          ([question]) => question === binding.question_key,
        );
        if (group) {
          if (!group[1].includes(optionValue)) group[1].push(optionValue);
        } else {
          room.g.push([binding.question_key, [optionValue]]);
        }
      }
    } else if (binding.question_key === "Civil work") {
      if (CW[label])
        CW_LIST[binding.room_type] = [
          ...new Set([...(CW_LIST[binding.room_type] || []), label]),
        ];
    } else if (binding.question_key === "Add more") {
      if (EX[label]) room.ex = [...new Set([...(room.ex || []), label])];
    } else {
      // _builtin: this option is priced by its dedicated code (TVU, P, BED_RATE ...),
      // so the generic "1 x rate" path must skip it.
      PLANNER_OPTION_RATES[optionValue] = {
        ...item,
        display_name: label,
        _builtin: true,
      };
      room.g = room.g.map(([question, options]) =>
        question === binding.question_key && options.includes(label)
          ? [question, [...new Set([...options, label])]]
          : [question, options],
      );
    }
  });
}

/* ===== Data ===== */
const BEDG = [
  ["Bed size", ["Single", "Queen", "King"]],
  ["Bed type", ["Standard", "Storage bed", "Hydraulic storage", "Upholstered"]],
  [
    "Bed back / headboard",
    [
      "Simple wooden",
      "Upholstered panel",
      "Full wall panelling",
      "Cane / fluted",
    ],
  ],
  ["Bedside tables", ["None", "1", "2"]],
];
const BEDS = { Single: [3.25, 6.25], Queen: [5, 6.5], King: [6, 6.5] };
const WSIZES = {
  3: "2-door",
  4: "2-door",
  5: "3-door",
  6: "3-door",
  7: "4-door",
  8: "4-door",
  9: "5-door",
};
const WTYPES = Object.keys(WARDROBE_RATE);
const WHEIGHTS = ["7 ft", "8 ft", "Floor to ceiling"];
const WH_FT = { "7 ft": 7, "8 ft": 8, "Floor to ceiling": 10 };

const TYPES = {
  master: {
    n: "Master Bedroom",
    bed: true,
    L: 13,
    W: 12,
    g: BEDG,
    ex: [
      "Dressing unit",
      "Study table",
      "TV unit",
      "Bench / ottoman",
      "Loft storage",
      "Wall mirror fitting",
      "TV back panelling",
    ],
  },
  child: {
    n: "Child Bedroom",
    bed: true,
    L: 11,
    W: 10,
    g: BEDG,
    ex: ["Study table", "Bookshelf", "Bunk bed", "Toy storage", "Loft storage"],
  },
  guest: {
    n: "Guest Bedroom",
    bed: true,
    L: 11,
    W: 10,
    g: BEDG,
    ex: [
      "Dressing unit",
      "Study table",
      "TV unit",
      "Loft storage",
      "TV back panelling",
      "Wall mirror fitting",
    ],
  },
  kitchen: {
    n: "Kitchen",
    L: 10,
    W: 8,
    g: [
      ["Layout", ["Straight", "L-shaped", "U-shaped", "Parallel", "Island"]],
      ["Finish", ["Laminate", "Acrylic", "PU / Lacquer"]],
    ],
    ex: [
      "Tall unit",
      "Pantry unit",
      "Loft cabinets",
      "Chimney & hob provision",
      "Breakfast counter",
      "Utility area",
      "Tandem box set (9 nos)",
      "Acrylic trolley shutters",
      "G profile handles (5 nos)",
      "Storage below sink",
    ],
  },
  living: {
    n: "Living Room",
    L: 16,
    W: 12,
    g: [
      ["Sofa", ["3-seater", "3+2 set", "L-shape sectional", "3+2+1 set"]],
      ["TV unit", ["Wall-mounted", "Floor standing"]],
    ],
    ex: [
      "TV back panelling (full wall)",
      "Centre table",
      "Console table",
      "Display unit",
      "Wall panelling",
      "Decorative charcoal panels",
    ],
  },
  dining: {
    n: "Dining Area",
    L: 11,
    W: 10,
    g: [
      [
        "Dining table",
        [
          "4-seater with chairs",
          "6-seater with chairs",
          "8-seater with chairs",
        ],
      ],
    ],
    ex: ["Crockery unit", "Bar unit", "Bench seating", "Wash basin unit"],
  },
  pooja: {
    n: "Pooja Unit",
    L: 4,
    W: 3,
    g: [
      ["Style", ["Wall-mounted", "Floor standing", "In-wall niche"]],
      ["Tier", ["Premium", "Luxury"]],
    ],
    ex: [
      "Jaali doors",
      "Storage drawers",
      "LED lighting",
      "Marble / stone base",
      "Bell & brass accents",
    ],
  },
  entrance: {
    n: "Entrance / Safety Door",
    L: 6,
    W: 5,
    g: [["Safety door", ["Standard", "Premium", "Luxury"]]],
    ex: [
      "Shoe rack",
      "Foyer bench",
      "Key & console unit",
      "Mirror panel",
      "Door wall laminate panelling",
    ],
  },
  terrace: { n: "Terrace Garden", L: 20, W: 15, g: [], ex: [] },
  electrical: { n: "Electrical Work", elec: true, L: 0, W: 0, g: [], ex: [] },
};

const STEPS = ["Rooms", "Package", "Furniture", "Your details", "Estimate"];

let uidCounter = 0;
const newRoom = (t) => ({
  id: ++uidCounter,
  t,
  L: TYPES[t].L,
  W: TYPES[t].W,
  sel: {},
  ex: [],
  cw: [],
  wards: TYPES[t].bed ? [{ ws: 0, wt: "", wh: "" }] : [],
  bw: 0,
  bl: 0,
  el: {},
});
const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
const amt = (n) =>
  Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
/* Single-digit whole numbers get ".0" (4 -> 4.0, 1 no -> 1.0 no).
   Numbers like 39, 6.5 or 1,600.00 are left untouched. */
const dec1 = (s) => String(s).replace(/(^|[^\d.,])(\d)(?![\d.,])/g, "$1$2.0");
const pdfDate = (d = new Date()) =>
  `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");
const roomTitle = (r) =>
  TYPES[r.t].elec ? TYPES[r.t].n : `${TYPES[r.t].n} (${r.L}×${r.W} ft)`;

/* Amount in words – Indian system (Lakh / Crore) */
const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];
const below100 = (n) =>
  n < 20
    ? ONES[n]
    : TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
function inWords(num) {
  let n = Math.round(num);
  if (!n) return "Zero";
  const parts = [];
  const cr = Math.floor(n / 1e7);
  n %= 1e7;
  const la = Math.floor(n / 1e5);
  n %= 1e5;
  const th = Math.floor(n / 1e3);
  n %= 1e3;
  const hu = Math.floor(n / 100);
  const rest = n % 100;
  if (cr) parts.push(`${below100(cr)} Crore`);
  if (la) parts.push(`${below100(la)} Lakh`);
  if (th) parts.push(`${below100(th)} Thousand`);
  if (hu) parts.push(`${ONES[hu]} Hundred`);
  if (rest) parts.push(below100(rest));
  return parts.join(" ");
}
const amountInWords = (n) => `Rupees ${inWords(n)} only`;

/* ===== Fit logic (feet) ===== */
/* Bed [width, length] in ft – the size the user typed, or the standard size of the chosen bed */
function getBed(r) {
  const s = r.sel["Bed size"];
  if (!s || !BEDS[s]) return null;
  return [r.bw || BEDS[s][0], r.bl || BEDS[s][1]];
}
function maxWardrobe(r, clear) {
  const b = getBed(r);
  if (!b) return null;
  const tables = (parseInt(r.sel["Bedside tables"]) || 0) * 1.5;
  let best = 0;
  if (r.W - b[0] - tables - 2 >= clear) best = Math.max(best, r.L - 1);
  if (r.L - b[1] - 2 >= clear) best = Math.max(best, r.W - 1);
  return Math.min(best, 9);
}
function fitWarnings(r) {
  const w = [];
  const a = r.L * r.W,
    m = Math.min(r.L, r.W);
  const b = getBed(r);
  if (b && (r.W < b[0] + 3 || r.L < b[1] + 3))
    w.push(
      `This room looks tight for a ${r.sel["Bed size"]} bed. Consider a smaller size.`,
    );
  if (
    !b &&
    (r.sel["Bed type"] ||
      r.sel["Bed back / headboard"] ||
      r.sel["Bedside tables"] > 0)
  )
    w.push(
      "Choose a bed size so the bed, bed back and bedside tables can be priced.",
    );
  const d = r.sel["Dining table"];
  if (d) {
    const match = /^(\d+)-seater\b/.exec(d);
    const n = match ? Number(match[1]) : 0;
    const need = { 4: [80, 8], 6: [110, 9], 8: [140, 10] }[n];
    if (need && (a < need[0] || m < need[1]))
      w.push(
        `A ${n}-seater needs about ${need[0]} sq ft with ${need[1]} ft on the shorter side.`,
      );
  }
  const s = r.sel["Sofa"];
  if (s === "L-shape sectional" && (a < 120 || m < 10))
    w.push(
      "L-shape sectional needs about 120 sq ft with 10 ft on the shorter side.",
    );
  if (s === "3+2+1 set" && a < 140)
    w.push("A 3+2+1 set needs about 140 sq ft.");
  return w;
}

/* ===== Pricing =====
   Every line item = { l: description, u: unit, q: quantity, r: rate, a: amount (q x r) }
   The selected package (pkgKey) multiplies furniture / carpentry rates. Civil work and electrical are never multiplied. */
function runFt(r, layout) {
  return {
    Straight: r.L,
    "L-shaped": r.L + r.W - 2,
    "U-shaped": r.L + 2 * r.W - 4,
    Parallel: 2 * r.L,
    Island: r.L + r.W - 2,
  }[layout];
}
const UNIT_TXT = {
  "SQ.FT": "sq ft",
  RFT: "rft",
  MTR: "mtr",
  EACH: "each",
  NO: "no",
  SET: "set",
  APPROX: "approx",
};
const f1 = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function priceRoom(r, pkgKey = "premium") {
  const items = [];
  const handledPlannerOptions = new Set();
  const mult = (PACKAGES[pkgKey] || PACKAGES.premium).mult;
  /* d = size text shown in the quotation (e.g. "6 x 6.5 ft"); m = material spec; amount = quantity x rate
     scale = false keeps the rate as it is (civil work, electrical) – everything else follows the package */
  const put = (l, u, q, rate, d, m, scale = true) => {
    const finalRate = scale && mult !== 1 ? Math.round(rate * mult) : rate;
    items.push({
      l,
      u,
      q,
      r: finalRate,
      a: q * finalRate,
      m: m || "",
      d:
        d ||
        (u === "NO"
          ? `${f1(q)} ${q === 1 ? "no" : "nos"}`
          : `${f1(q)} ${UNIT_TXT[u] || ""}`.trim()),
    });
  };
  const putArea = (l, a, b, rate, m, scale) =>
    put(
      l,
      "SQ.FT",
      Math.round(a * b * 100) / 100,
      rate,
      `${f1(a)} x ${f1(b)} ft`,
      m,
      scale,
    );
  const putPlannerOption = (value, quantityOverride) => {
    const item = PLANNER_OPTION_RATES[value];
    if (!item || handledPlannerOptions.has(value)) return false;
    if (item._builtin) return false; // built-in options are priced by their dedicated code
    const defaultQuantity = Number(item.default_quantity);
    const quantity =
      quantityOverride ??
      (Number.isFinite(defaultQuantity) && defaultQuantity > 0
        ? defaultQuantity
        : 1);
    const rate = Number(item.base_rate) + Number(item.installation_rate || 0);
    if (!Number.isFinite(rate)) return false;
    const keepRate =
      item.rate_group === "civil" || item.rate_group === "electrical";
    put(
      item.display_name,
      item.unit || "NO",
      quantity,
      rate,
      undefined,
      item.specification || item.material || "",
      !keepRate,
    );
    handledPlannerOptions.add(value);
    return true;
  };
  const total = () => items.reduce((s, i) => s + i.a, 0);

  /* Electrical work (whole house) – supply & install shown separately like the reference */
  if (TYPES[r.t].elec) {
    ELECTRICAL.forEach((e) => {
      const q = (r.el && r.el[e.id]) || 0;
      if (q > 0) {
        put(
          e.l,
          e.u,
          q,
          e.s + e.i,
          `${f1(q)} ${e.u === "EACH" ? "nos" : "mtr"}`,
          undefined,
          false,
        );
        const it = items[items.length - 1];
        it.sa = q * e.s;
        it.ia = q * e.i;
      }
    });
    (r.ex || []).forEach((value) => putPlannerOption(value));
    return { items, total: total() };
  }

  const bed = getBed(r);

  if (bed) {
    const type = r.sel["Bed type"] || "Standard";
    const bedSpec =
      type === "Hydraulic storage"
        ? `${PLY}; Ebco hydraulic pump`
        : type === "Upholstered"
          ? FOAM
          : PLY;
    const bedProduct = BED_CATALOGUE[type];
    const bedLabel = `Bed (${r.sel["Bed size"]} size)${type === "Standard" ? "" : ` with ${type.toLowerCase()}`}`;
    const configuredBedRate = PLANNER_OPTION_RATES[type];
    if (
      configuredBedRate?.calculation_type === "bed_area" &&
      String(configuredBedRate.unit).toUpperCase() === "SQ.FT"
    ) {
      putArea(
        bedLabel,
        bed[0],
        bed[1],
        Number(configuredBedRate.base_rate) +
          Number(configuredBedRate.installation_rate || 0),
        bedSpec,
      );
      handledPlannerOptions.add(type);
    } else if (!putPlannerOption(type)) {
      // Catalogue products provide the customer-facing image/design card only.
      // The commercial rate always comes from the Planner Rate Master.
      if (bedProduct && bedProduct.unit && bedProduct.unit !== "SQ.FT") {
        put(
          bedLabel,
          bedProduct.unit,
          1,
          BED_RATE[type],
          `${f1(bed[0])} x ${f1(bed[1])} ft`,
          bedSpec,
        );
      } else {
        putArea(bedLabel, bed[0], bed[1], BED_RATE[type], bedSpec);
      }
    }

    const bb = r.sel["Bed back / headboard"];
    if (bb) {
      if (!putPlannerOption(bb)) {
        const [rate, h] = BEDBACK[bb];
        if (h)
          putArea(
            `Bed back / headboard: ${bb}`,
            bed[0],
            h,
            rate,
            bb === "Upholstered panel" ? FOAM : PLY,
          );
      }
    }
    const bt = parseInt(r.sel["Bedside tables"]) || 0;
    if (bt)
      put(
        `Bed side tables x ${bt}`,
        "SQ.FT",
        bt * BEDSIDE_SQFT,
        PLANNER_RATE_OVERRIDES.bedsideRate,
        `2 x 2 ft x ${bt} nos`,
        PLY,
      );
  }

  if (r.sel["Sofa"] && !hasCustomOption(r.sel["Sofa"]))
    put(`Sofa: ${r.sel["Sofa"]}`, "SET", 1, P[`Sofa|${r.sel["Sofa"]}`] || 0);

  if (r.sel["TV unit"] && !hasCustomOption(r.sel["TV unit"])) {
    const [a, b, rate] = TVU[r.sel["TV unit"]];
    putArea(`TV unit storage – ${r.sel["TV unit"]}`, a, b, rate, PLY);
  }

  if (r.sel["Dining table"] && !hasCustomOption(r.sel["Dining table"]))
    put(
      `Dining table: ${r.sel["Dining table"]}`,
      "SET",
      1,
      P[`Dining table|${r.sel["Dining table"]}`] || 0,
    );

  if (r.sel["Safety door"] && !hasCustomOption(r.sel["Safety door"])) {
    put(
      `Safety main door (${r.sel["Safety door"]})`,
      "APPROX",
      1,
      P[`Safety door|${r.sel["Safety door"]}`] || 0,
      "1 door (approx)",
    );
  }

  if (r.sel["Layout"]) {
    const f = r.sel["Finish"] || "Laminate";
    const run = Math.round(runFt(r, r.sel["Layout"]) * 10) / 10;
    if (!putPlannerOption(f))
      put(
        `Kitchen ${r.sel["Layout"]}, ${f}${r.sel["Finish"] ? "" : " (default)"}`,
        "RFT",
        run,
        KITCHEN_RATE[f],
        `${f1(run)} running ft`,
      );
    if (r.sel["Layout"] === "Island")
      put("Island unit", "NO", 1, PLANNER_RATE_OVERRIDES.islandExtra);
  }

  if (r.sel["Style"]) {
    const tier = r.sel["Tier"] || "Premium";
    const [a, b] = POOJA_DIM[r.sel["Style"]];
    const poojaKey = `${r.sel["Style"]
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}:${tier
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}`;
    const poojaRate =
      PLANNER_RATE_OVERRIDES.pooja[poojaKey] ?? POOJA_RATE * POOJA_TIER[tier];
    putArea(
      `Pooja unit: ${r.sel["Style"]}, ${tier}${r.sel["Tier"] ? "" : " (default)"}`,
      a,
      b,
      poojaRate,
    );
  }

  /* Wardrobes – a room can have more than one (e.g. walk-in closet) */
  const wards = r.wards || [];
  wards.forEach((w, i) => {
    if (!w.ws) return;
    const h = WH_FT[w.wh || "7 ft"];
    const nm = wards.length > 1 ? `Wardrobe ${i + 1}: ` : "";
    if (w.wt) {
      putArea(
        `${nm}${w.wt} wardrobe L ${w.ws}'0" x W 2'0" x H ${h}'0", ${WSIZES[w.ws]}, two drawers`,
        w.ws,
        h,
        WARDROBE_RATE[w.wt],
        `${PLY}, Hettich fitting`,
      );
    } else {
      putArea(`${nm}Wardrobe ${w.ws} ft – choose a type to price`, w.ws, h, 0);
    }
  });

  r.ex.forEach((e) => {
    if (putPlannerOption(e)) return;
    const x = EX[e];
    if (!x) return put(e, "NO", 1, 0);
    if (x[0] === "SQ.FT") putArea(e, x[1], x[2], x[3], EX_SPEC[e] || PLY);
    else put(e, x[0], x[1], x[2], undefined, EX_SPEC[e]);
  });

  const perim = 2 * (r.L + r.W);
  (r.cw || []).forEach((c) => {
    if (putPlannerOption(c)) return;
    const d = CW[c];
    if (!d) return;
    const lbl = `Civil: ${c}${d.u === "wall" ? " (wall area)" : d.u === "dado" ? " (dado area)" : ""}`;
    if (d.u === "area") putArea(lbl, r.L, r.W, d.r, undefined, false);
    else if (d.u === "wall") putArea(lbl, perim, 9, d.r, undefined, false);
    else if (d.u === "dado") putArea(lbl, perim, 3, d.r, undefined, false);
    else if (d.u === "run") {
      const run = Math.round(runFt(r, r.sel["Layout"] || "Straight") * 10) / 10;
      put(lbl, "RFT", run, d.r, `${f1(run)} running ft`, undefined, false);
    } else put(lbl, "NO", 1, d.r, undefined, undefined, false);
  });

  Object.values(r.sel || {}).forEach((value) => putPlannerOption(value));
  (r.ex || []).forEach((value) => putPlannerOption(value));

  return { items, total: total() };
}

/* ============================================================
   PDF QUOTATION  (same look as before – room tables now show
   S.No | Description | Size (L x W) | Quantity | Rate | Amount, with grid lines and left-aligned text)
   Size / Quantity / Rate / Amount columns are right-aligned (end se aligned) and
   single-digit numbers in Size / Quantity get a trailing ".0".
   Material descriptions are no longer printed per line – the selected package's
   specification table is printed once in section 3.0.
   NOTE: jsPDF's built-in fonts can't draw the ₹ sign, so the PDF
   uses "INR" / "Rs." instead.
   ============================================================ */
function buildQuotationPdf({
  contact,
  priced,
  interior,
  electrical,
  subtotal,
  installation,
  totalCost,
  discount,
  gst,
  grandTotal,
  ref,
  pkgInfo,
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 15;
  const now = new Date();

  /* Page background colour – painted on page 1 and on every page added later */
  const bg = () => {
    doc.setFillColor(...PDF_PAGE_BG);
    doc.rect(0, 0, W, H, "F");
  };
  const addPageOrig = doc.addPage.bind(doc);
  doc.addPage = (...args) => {
    const r = addPageOrig(...args);
    bg();
    return r;
  };
  bg();

  /* Header – logo + Italiana wordmark on the left, QUOTATION on the right, accent rule, then Ref / Date strip */
  let serif = "times"; // fallback if the Italiana font can't be loaded
  try {
    doc.addFileToVFS("Italiana-Regular.ttf", ITALIANA_TTF_BASE64);
    doc.addFont("Italiana-Regular.ttf", "Italiana", "normal");
    serif = "Italiana";
  } catch (e) {

    /* keep fallback font */
  }

  const LOGO_H = 15;
  const LOGO_W = LOGO_H * LOGO_ASPECT;
  try {
    doc.addImage(
      `data:image/png;base64,${LOGO_PNG_BASE64}`,
      "PNG",
      M,
      13,
      LOGO_W,
      LOGO_H,
    );
  } catch (e) {
    /* skip logo if it can't be drawn */
  }

  // Brand name + tagline
  doc
    .setFont(serif, "normal")
    .setFontSize(26)
    .setTextColor(...PDF_DARK);
  doc.text("Dwellora Homes", M + LOGO_W + 4, 24);
  doc
    .setFont("helvetica", "normal")
    .setFontSize(7.5)
    .setTextColor(...PDF_GREY);
  if (typeof doc.setCharSpace === "function") doc.setCharSpace(0.5);
  doc.text("A VENTURE BY DWELLORA DESIGNS LLP", M + LOGO_W + 4.3, 29.5);
  if (typeof doc.setCharSpace === "function") doc.setCharSpace(0);

  // Document title
  doc
    .setFont("helvetica", "normal")
    .setFontSize(24)
    .setTextColor(...PDF_ORANGE);
  doc.text("Quotation", W - M, 24, { align: "right" });

  // Accent rule (orange line with a short dark segment at the start)
  doc.setFillColor(...PDF_ORANGE).rect(M, 35, W - 2 * M, 0.7, "F");
  doc.setFillColor(...PDF_DARK).rect(M, 35, 32, 0.7, "F");

  // Ref / Date strip
  const infoRow = (label, value, x, align) => {
    if (align === "right") {
      doc
        .setFont("helvetica", "normal")
        .setFontSize(10)
        .setTextColor(...PDF_DARK);
      const vw = doc.getTextWidth(value);
      doc.text(value, x, 44, { align: "right" });
      doc
        .setFont("helvetica", "bold")
        .setFontSize(8)
        .setTextColor(...PDF_GREY);
      doc.text(label, x - vw - 2.5, 44, { align: "right" });
    } else {
      doc
        .setFont("helvetica", "bold")
        .setFontSize(8)
        .setTextColor(...PDF_GREY);
      doc.text(label, x, 44);
      const lw = doc.getTextWidth(label);
      doc
        .setFont("helvetica", "normal")
        .setFontSize(10)
        .setTextColor(...PDF_DARK);
      doc.text(value, x + lw + 2.5, 44);
    }
  };
  infoRow("REF", ref, M, "left");
  infoRow("DATE", pdfDate(now), W - M, "right");

  const section = (title, y) => {
    if (y > H - 45) {
      doc.addPage();
      y = 20;
    }
    doc.setFillColor(247, 248, 250).rect(M, y, W - 2 * M, 9, "F");
    doc.setFillColor(...PDF_DARK).rect(M, y, 1.2, 9, "F");
    doc
      .setFont("helvetica", "bold")
      .setFontSize(11)
      .setTextColor(...PDF_DARK);
    doc.text(title, M + 4, y + 6);
    return y + 13;
  };
  const after = (gap = 8) => doc.lastAutoTable.finalY + gap;

  /* 1.0 Project overview */
  let y;

  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, 54, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, 54, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("1.0 PROJECT OVERVIEW", M + 4, 60);

  y = 67;

  const overview = [
    [
      "Client Name:",
      contact.name || "-",
      "Property Type:",
      contact.property || "-",
    ],
    ["Mobile No.:", contact.phone || "-", "Email:", contact.email || "-"],
    ["Floor:", contact.floor || "-", "Site Location:", contact.location || "-"],
    ["Package:", { content: `${pkgInfo.name} Package`, colSpan: 3 }],
  ];
  if (contact.notes)
    overview.push(["Notes:", { content: contact.notes, colSpan: 3 }]);
  autoTable(doc, {
    startY: y,
    body: overview,
    theme: "plain",
    styles: { fontSize: 10, textColor: PDF_DARK, cellPadding: 1.8 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 30 },
      2: { fontStyle: "bold", cellWidth: 32 },
    },
    margin: { left: M, right: M },
  });
  y = after(6);

  /* 2.0 Room-wise interior estimate */
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, y, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, y, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("2.0 ROOM-WISE INTERIOR ESTIMATE", M + 4, y + 6);

  y += 13;

  const GRID_LINE = [200, 190, 175];
  const baseStyles = {
    fontSize: 9,
    textColor: PDF_DARK,
    cellPadding: 2,
    lineColor: GRID_LINE,
    lineWidth: { top: 0.2, bottom: 0.2, left: 0, right: 0 }, // vertical lines OFF
    valign: "middle",
    halign: "left",
  };

  /* Alternate column shades (very light) */
  const COL_A = [252, 249, 243]; // lighter cream
  const COL_B = [246, 238, 226]; // light beige-orange tint
  const bandColumns = (d) => {
    if (d.section === "body") {
      d.cell.styles.fillColor = d.column.index % 2 === 0 ? COL_A : COL_B;
    }
  };

  const subHead = (d) => {
    if (d.section === "head" && d.row.index === 1) {
      d.cell.styles.fillColor = [243, 236, 225];
      d.cell.styles.textColor = PDF_DARK;
    }
    bandColumns(d);
  };

  priced.forEach((x, i) => {
    const title = `2.${i + 1}  ${roomTitle(x.room)}`;
    if (TYPES[x.room.t].elec) {
      /* Electrical: Supply / Install / Total shown separately, like the reference */
      const sTot = x.items.reduce((t, it) => t + (it.sa || 0), 0);
      const iTot = x.items.reduce((t, it) => t + (it.ia || 0), 0);
      autoTable(doc, {
        startY: y,
        head: [
          [{ content: title, colSpan: 7 }],
          [
            "S.No",
            "Description",
            "Qty",
            "Rate",
            "Supply",
            "Install",
            "Total (INR)",
          ],
        ],
        body: x.items.length
          ? x.items.map((it, n) => [
              n + 1,
              it.l,
              it.d || "",
              amt(it.r),
              amt(it.sa || 0),
              amt(it.ia || 0),
              amt(it.a),
            ])
          : [["", "No selections made", "", "", "", "", "-"]],
        foot: [
          [
            { content: `${TYPES[x.room.t].n} total`, colSpan: 4 },
            amt(sTot),
            amt(iTot),
            amt(x.total),
          ],
        ],
        theme: "grid",
        headStyles: {
          fillColor: PDF_PURPLE,
          textColor: 255,
          fontStyle: "bold",
          fontSize: 9.5,
          halign: "left",
        },
        bodyStyles: { fillColor: false },
        footStyles: {
          fillColor: [243, 236, 225],
          textColor: PDF_DARK,
          fontStyle: "bold",
          fontSize: 9,
          halign: "left",
        },
        styles: { ...baseStyles, fontSize: 8.5 },
        columnStyles: {
          0: { cellWidth: 13 },
          2: { cellWidth: 20 },
          3: { cellWidth: 17 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 26 },
        },
        didParseCell: subHead,
        margin: { left: M, right: M, bottom: 20 },
      });
      y = after(8);
      return;
    }
    autoTable(doc, {
      startY: y,
      head: [
        [{ content: title, colSpan: 6 }],
        [
          "S.No",
          "Description",
          "Size (L x W)",
          "Quantity",
          "Rate (INR)",
          "Amount (INR)",
        ],
      ],
      body: x.items.length
        ? x.items.map((it, n) => [
            n + 1,
            it.l,
            dec1(it.d || ""), // single digit -> 4.0
            dec1(`${fmtQty(it.q)} ${UNIT_TXT[it.u] || ""}`.trim()), // single digit -> 4.0 sq ft
            it.r ? `${amt(it.r)} / ${UNIT_TXT[it.u] || "unit"}` : "-",
            it.a ? amt(it.a) : "-",
          ])
        : [["", "No selections made", "", "", "", "-"]],
      foot: [
        [{ content: `${TYPES[x.room.t].n} total`, colSpan: 5 }, amt(x.total)],
      ],
      theme: "grid",
      headStyles: {
        fillColor: PDF_PURPLE,
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10,
        halign: "left",
      },
      bodyStyles: { fillColor: false },
      footStyles: {
        fillColor: [243, 236, 225],
        textColor: PDF_DARK,
        fontStyle: "bold",
        fontSize: 10,
        halign: "left",
      },
      styles: baseStyles,
      columnStyles: {
        0: { cellWidth: 12 },
        2: { cellWidth: 24 },
        3: { cellWidth: 22 },
        4: { cellWidth: 29 },
        5: { cellWidth: 27 },
      },
      didParseCell: (d) => {
        subHead(d);
        // Size, Quantity, Rate and Amount – all right aligned so values line up from the end
        if (d.column.index >= 2) d.cell.styles.halign = "right";
      },
      margin: { left: M, right: M, bottom: 20 },
    });
    y = after(8);
  });

  /* 3.0 Package specifications – the selected package's materials, printed once instead of per line */
  if (y > H - 70) {
    doc.addPage();
    y = 20;
  }
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, y, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, y, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("3.0 PACKAGE SPECIFICATIONS", M + 4, y + 6);

  y += 13;

  autoTable(doc, {
    startY: y,
    head: [
      [{ content: `${pkgInfo.name} Package – Material Specifications`, colSpan: 2 }],
      ["Category", "Specification"],
    ],
    body: pkgInfo.specs.map(([category, spec]) => [category, spec]),
    theme: "grid",
    headStyles: {
      fillColor: PDF_PURPLE,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 10,
      halign: "left",
    },
    bodyStyles: { fillColor: false },
    styles: baseStyles,
    columnStyles: { 0: { cellWidth: 52, fontStyle: "bold" } },
    didParseCell: subHead,
    margin: { left: M, right: M, bottom: 20 },
  });
  y = after(8);

  /* 4.0 Cost summary – if the whole table won't fit on this page, start a new page so the title stays with the table */
  if (y > H - 105) {
    doc.addPage();
    y = 20;
  }
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, y, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, y, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("4.0 COST SUMMARY", M + 4, y + 6);

  y += 13;

  autoTable(doc, {
    startY: y,
    body: [
      ["Interior turnkey project cost", amt(interior)],
      ["Electrical work approx. including fittings", amt(electrical)],
      ["Total", amt(subtotal)],
      [
        `Architectural consultancy fees (${PLANNER_SETTINGS.installationPct * 100}% on total cost of project)`,
        amt(installation),
      ],
      ["Total cost", amt(totalCost)],
      ["Discount", amt(discount)],
      [`GST (${plannerGstLabel()})`, amt(gst)],
    ],
    foot: [
      [
        `Grand Total (${PLANNER_SETTINGS.gst ? "incl." : "excl."} GST)`,
        amt(grandTotal),
      ],
    ],
    theme: "plain",
    styles: {
      fontSize: 10,
      textColor: PDF_GREY,
      cellPadding: 2.4,
      lineColor: [235, 235, 235],
      lineWidth: { bottom: 0.2 },
    },
    footStyles: {
      fillColor: [243, 236, 225],
      textColor: PDF_DARK,
      fontStyle: "bold",
      fontSize: 12,
    },
    columnStyles: { 1: { halign: "right", cellWidth: 45 } },
    didParseCell: (d) => {
      if (d.column.index === 1) d.cell.styles.halign = "right"; // body + Grand Total row aligned in one line
    },
    margin: { left: M, right: M, bottom: 20 },
  });
  y = after(6);
  if (y > H - 30) {
    doc.addPage();
    y = 20;
  }
  doc
    .setFont("helvetica", "bold")
    .setFontSize(10)
    .setTextColor(...PDF_DARK);
  const wordLines = doc.splitTextToSize(
    `In words: ${amountInWords(grandTotal)}`,
    W - 2 * M,
  );
  doc.text(wordLines, M, y);
  y += wordLines.length * 5 + 5;

  /* 5.0 Payment Milestones */
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, y, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, y, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("5.0 PAYMENT MILESTONES", M + 4, y + 6);

  y += 13;

  autoTable(doc, {
    startY: y,
    head: [["Stage", "%", "Amount (INR)"]],
    body: PAYMENT_MILESTONES.map(([s, p]) => [
      s,
      `${p}%`,
      amt((grandTotal * p) / 100),
    ]),
    foot: [["Total", "100%", amt(grandTotal)]],
    theme: "plain",
    headStyles: { fillColor: PDF_PURPLE, textColor: 255, fontStyle: "bold" },
    footStyles: {
      fillColor: [243, 236, 225],
      textColor: PDF_DARK,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 10,
      textColor: PDF_DARK,
      cellPadding: 2.2,
      lineColor: [235, 235, 235],
      lineWidth: { bottom: 0.2 },
    },
    columnStyles: {
      1: { halign: "center", cellWidth: 20 },
      2: { halign: "right", cellWidth: 45 },
    },
    didParseCell: (d) => {
      if (d.column.index === 1) d.cell.styles.halign = "center"; // % column: header, rows, total
      if (d.column.index === 2) d.cell.styles.halign = "right"; // Amount column: header, rows, total
    },
    margin: { left: M, right: M, bottom: 20 },
  });
  y = after(8);

  /* 6.0 Terms and Conditions */
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, y, W - 2 * M, 9, "F");

  // Left side vertical line
  doc.setFillColor(...PDF_DARK);
  doc.rect(M, y, 1.2, 9, "F");

  // White heading text
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);

  doc.text("6.0 TERMS & CONDITIONS", M + 4, y + 6);

  y += 13;

  doc
    .setFont("helvetica", "normal")
    .setFontSize(9.5)
    .setTextColor(...PDF_DARK);
  TERMS.forEach((t, i) => {
    const lines = doc.splitTextToSize(`${i + 1}. ${t}`, W - 2 * M - 4);
    if (y + lines.length * 5 > H - 20) {
      doc.addPage();
      y = 20;
    }
    doc.text(lines, M + 2, y);
    y += lines.length * 5 + 1.5;
  });

  /* Signatures */
  if (y > H - 55) {
    doc.addPage();
    y = 20;
  }
  y += 18;
  doc.setDrawColor(150).line(M, y, M + 55, y);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text("Client Signature", M, y + 5);
  doc
    .setFont("helvetica", "bold")
    .text("Ar. Gajanan V. Shetkar", W - M, y, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text("Founder & Principal Architect | CoA:CA/2014/64577", W - M, y + 5, {
    align: "right",
  });

  /* Footer on every page */
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(220).line(M, H - 15, W - M, H - 15);
    doc
      .setFont("helvetica", "bold")
      .setFontSize(8)
      .setTextColor(...PDF_DARK);
    doc.text(
      "DWELLORA DESIGNS LLP  |  ARCHITECTURE | INTERIOR | TURNKEY CONSTRUCTION",
      W / 2,
      H - 10.5,
      { align: "center" },
    );
    doc.setFont("helvetica", "normal").setTextColor(...PDF_GREY);
    doc.text(
      "Office: 601, The Stellar, Bhumkar Chowk, Wakad-411057  |  M: +91 9158914222  |  E: dwellorahomes@gmail.com  |  W: www.dwellorahomes.com",
      W / 2,
      H - 6.5,
      { align: "center" },
    );
    doc.text(`Page ${p} of ${pages}`, W - M, 10, { align: "right" });
  }

  const slug = (contact.name || "client")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const fileName = `dwellora-quotation-${slug}-${pdfDate(now).replace(/\./g, "").slice(0, 4)}${String(now.getFullYear()).slice(2)}.pdf`;
  return { doc, fileName };
}

/* ===== Small UI pieces ===== */
function Chip({ label, on, dis, onClick }) {
  return (
    <button
      type="button"
      className={`fp-chip${on ? " on" : ""}${dis ? " dis" : ""}`}
      disabled={dis}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function catalogueOption(product, label) {
  if (product.planner_key && PLANNER_OPTION_RATES[product.planner_key])
    return product.planner_key;
  if (label === "Bed type") {
    const bedTypes = Object.keys(BED_RATE);
    const names = [product.material, product.name].filter(Boolean);
    for (const value of names) {
      const normalized = value.trim().toLowerCase().replace(/\s+/g, " ");
      const match = bedTypes.find(
        (type) =>
          normalized === type.toLowerCase() ||
          normalized === `${type.toLowerCase()} bed`,
      );
      if (match) return match;
    }
    if (
      !product.material?.trim() &&
      product.name.trim().toLowerCase() === "bed"
    )
      return "Standard";
    return "";
  }
  if (label !== "Sofa") return product.material;
  if (
    ["3-seater", "3+2 set", "L-shape sectional", "3+2+1 set"].includes(
      product.material,
    )
  )
    return product.material;
  const name = product.name.toLowerCase();
  if (name.includes("3 seater")) return "3-seater";
  if (name.includes("3+2")) return "3+2 set";
  return product.name;
}

function plannerOptionLabel(value) {
  return PLANNER_OPTION_RATES[value]?.display_name || value;
}

function ProductChoices({ label, options, selected, onSelect, products, mult = 1 }) {
  const category =
    label === "Sofa"
      ? "Sofa"
      : label === "Finish"
        ? "Kitchen"
        : label === "Bed type"
          ? "Bed"
          : null;
  /* the price on the card follows the selected package, same as the quotation */
  const adj = (n) => (mult !== 1 ? Math.round(n * mult) : n);
  const cards = category
    ? options
        .map((option) => {
          const product = products.find(
            (item) =>
              item.category === category &&
              (!String(item.planner_key || "").startsWith("product:") ||
                PLANNER_OPTION_RATES[item.planner_key]) &&
              catalogueOption(item, label) === option,
          );
          const rate = PLANNER_OPTION_RATES[option];
          if (!product && !rate) return null;
          return {
            id: product?.id || rate?.id || option,
            option,
            name: product?.name || rate?.display_name || option,
            image_url: rate?.image_url || product?.image_url,
            price: adj(
              rate
                ? Number(rate.base_rate) + Number(rate.installation_rate || 0)
                : Number(product.price),
            ),
            unit: rate?.unit || product?.unit,
          };
        })
        .filter(Boolean)
    : [];
  if (!cards.length) return null;
  return (
    <div className="fp-product-grid">
      {cards.map((product) => {
        const option = product.option;
        return (
          <button
            className={`fp-product-choice ${selected === option ? "is-selected" : ""}`}
            key={product.id}
            onClick={() => onSelect(option)}
            type="button"
          >
            {product.image_url ? (
              <img alt="" src={`${API_BASE_URL}${product.image_url}`} />
            ) : (
              <span className="fp-product-placeholder">Image coming soon</span>
            )}
            <strong>{product.name}</strong>
            <small>
              {inr(product.price)} / {product.unit}
            </small>
            <em>{selected === option ? "Selected" : "Select"}</em>
          </button>
        );
      })}
    </div>
  );
}

function RoomCard({ room, onChange, onRemove, products, pkg }) {
  const cfg = TYPES[room.t];
  const priced = useMemo(() => priceRoom(room, pkg), [room, products, pkg]);
  const warnings = useMemo(() => fitWarnings(room), [room]);

  const setSel = (k, v) => {
    const sel = { ...room.sel };
    if (sel[k] === v) delete sel[k];
    else sel[k] = v;
    let next = { ...room, sel };
    if (k === "Bed size") {
      const d = sel[k] ? BEDS[sel[k]] : null;
      next.bw = d ? d[0] : 0;
      next.bl = d ? d[1] : 0;
    }
    next = refit(next);
    onChange(next);
  };
  const toggleEx = (v) => {
    const ex = room.ex.includes(v)
      ? room.ex.filter((x) => x !== v)
      : [...room.ex, v];
    onChange({ ...room, ex });
  };
  const toggleCw = (v) => {
    let cw = room.cw.includes(v)
      ? room.cw.filter((x) => x !== v)
      : [...room.cw, v];
    if (!room.cw.includes(v) && FLOORING.includes(v))
      cw = cw.filter((x) => x === v || !FLOORING.includes(x));
    onChange({ ...room, cw });
  };
  const setDim = (k, v) => {
    const n = Math.max(2, parseFloat(v) || room[k]);
    onChange(refit({ ...room, [k]: n }));
  };
  const setBedDim = (k, v) => {
    const n = Math.min(10, Math.max(2, parseFloat(v) || room[k]));
    onChange(refit({ ...room, [k]: n }));
  };
  const setWard = (i, patch) =>
    onChange(
      refit({
        ...room,
        wards: room.wards.map((w, j) => (j === i ? { ...w, ...patch } : w)),
      }),
    );
  const setWs = (i, v) =>
    setWard(i, room.wards[i].ws === v ? { ws: 0, wt: "", wh: "" } : { ws: v });
  const setWt = (i, v) => setWard(i, { wt: room.wards[i].wt === v ? "" : v });
  const setWh = (i, v) => setWard(i, { wh: room.wards[i].wh === v ? "" : v });
  const addWard = () =>
    onChange({ ...room, wards: [...room.wards, { ws: 0, wt: "", wh: "" }] });
  const removeWard = (i) =>
    onChange({ ...room, wards: room.wards.filter((_, j) => j !== i) });
  const setEl = (id, v) =>
    onChange({
      ...room,
      el: { ...room.el, [id]: Math.max(0, parseFloat(v) || 0) },
    });
  const fillTypical = () =>
    onChange({
      ...room,
      el: Object.fromEntries(ELECTRICAL.map((e) => [e.id, e.d])),
    });
  const clearEl = () => onChange({ ...room, el: {} });

  /* ---- Electrical work card (whole house) ---- */
  if (cfg.elec) {
    return (
      <div className="fp-card">
        <div className="fp-room-h">
          <h3>{cfg.n} (whole house)</h3>
          <div className="fp-dims">
            <button type="button" className="fp-x" onClick={onRemove}>
              Remove
            </button>
          </div>
        </div>
        <div className="fp-note">
          Enter the quantity for each item. Rate = supply + installation. Not
          sure? Tap "Fill typical quantities" (based on a standard 3 BHK) – our
          team will confirm after a site visit.
        </div>
        <div className="fp-actions" style={{ marginTop: 10 }}>
          <button type="button" className="fp-btn alt" onClick={fillTypical}>
            Fill typical quantities
          </button>
          <button type="button" className="fp-btn alt" onClick={clearEl}>
            Clear
          </button>
        </div>
        {ELECTRICAL.map((e) => (
          <div key={e.id} className="fp-el">
            <div className="fp-el-l">
              {e.l}
              <div className="fp-qty">
                Supply ₹{e.s} + Install ₹{e.i} = ₹{e.s + e.i} per{" "}
                {e.u.toLowerCase()}
              </div>
            </div>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="0"
              value={room.el[e.id] || ""}
              onChange={(ev) => setEl(e.id, ev.target.value)}
            />
            <span className="fp-qty">{e.u}</span>
          </div>
        ))}
        {room.ex.length > 0 && (
          <>
            <div className="fp-gt">Additional electrical options</div>
            <div className="fp-chips">
              {room.ex.map((value) => (
                <Chip
                  key={value}
                  label={plannerOptionLabel(value)}
                  on={room.ex.includes(value)}
                  onClick={() => toggleEx(value)}
                />
              ))}
            </div>
          </>
        )}
        <div className="fp-rtot">
          <span>{cfg.n} estimate</span>
          <span>{inr(priced.total)}</span>
        </div>
      </div>
    );
  }

  const maxW = cfg.bed ? maxWardrobe(room, 2.5) : null;
  const maxWOpen = cfg.bed ? maxWardrobe(room, 3.5) : null;
  const sizes = Object.keys(WSIZES).map(Number);
  const okSizes = maxW !== null ? sizes.filter((s) => s <= maxW) : [];
  const recommended = okSizes.length ? okSizes[okSizes.length - 1] : 0;
  const area = room.L * room.W,
    shortSide = Math.min(room.L, room.W);

  return (
    <div className="fp-card">
      <div className="fp-room-h">
        <h3>{cfg.n}</h3>
        <div className="fp-dims">
          <input
            type="number"
            min="2"
            max="60"
            step="0.5"
            value={room.L}
            onChange={(e) => setDim("L", e.target.value)}
          />
          <span>×</span>
          <input
            type="number"
            min="2"
            max="60"
            step="0.5"
            value={room.W}
            onChange={(e) => setDim("W", e.target.value)}
          />
          <span>ft</span>
          <button type="button" className="fp-x" onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>

      {cfg.g.map(([k, opts]) => (
        <div key={k}>
          <div className="fp-gt">{k}</div>
          <ProductChoices
            label={k}
            options={opts}
            selected={room.sel[k]}
            onSelect={(v) => setSel(k, v)}
            products={products}
            mult={(PACKAGES[pkg] || PACKAGES.premium).mult}
          />
          <div className="fp-chips">
            {(k === "Sofa" || k === "Finish") &&
            products.some(
              (product) =>
                product.category === (k === "Sofa" ? "Sofa" : "Kitchen"),
            )
              ? opts
                  .filter((v) =>
                    products.some(
                      (product) =>
                        product.category ===
                          (k === "Sofa" ? "Sofa" : "Kitchen") &&
                        catalogueOption(product, k) === v,
                    ),
                  )
                  .map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      on={room.sel[k] === v}
                      onClick={() => setSel(k, v)}
                    />
                  ))
              : opts.map((v) => (
                  <Chip
                    key={v}
                    label={plannerOptionLabel(v)}
                    on={room.sel[k] === v}
                    onClick={() => setSel(k, v)}
                  />
                ))}
          </div>
          {k === "Bed size" && room.sel[k] && (
            <>
              <div className="fp-gt">Bed size you need</div>
              <div className="fp-dims">
                <input
                  type="number"
                  min="2"
                  max="10"
                  step="0.25"
                  value={room.bw}
                  onChange={(e) => setBedDim("bw", e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  min="2"
                  max="10"
                  step="0.25"
                  value={room.bl}
                  onChange={(e) => setBedDim("bl", e.target.value)}
                />
                <span>ft (width × length)</span>
              </div>
              <div className="fp-note">
                Standard {room.sel[k]} bed is {BEDS[room.sel[k]][0]} ×{" "}
                {BEDS[room.sel[k]][1]} ft. Change it if you need a different
                size – the wardrobe fit is checked using this size.
              </div>
            </>
          )}
        </div>
      ))}

      {cfg.bed && (
        <>
          {maxW === null ? (
            <>
              <div className="fp-gt">Wardrobe</div>
              <div className="fp-note">
                Choose a bed size first – we use it to check the free wall space
                for your wardrobe.
              </div>
            </>
          ) : (
            <>
              {recommended ? (
                <div className="fp-note ok">
                  Recommended: {recommended} ft {WSIZES[recommended]} wardrobe –
                  best fit for your bed and room size.
                </div>
              ) : (
                <div className="fp-note warn">
                  Not enough free wall for a standard wardrobe. Try a smaller
                  bed, fewer bedside tables, or ask us about a custom solution.
                </div>
              )}
              {room.wards.map((w, i) => (
                <div key={i}>
                  <div className="fp-gt">
                    Wardrobe{room.wards.length > 1 ? ` ${i + 1}` : ""} width
                    (fits up to {maxW >= 3 ? Math.floor(maxW) : 0} ft in this
                    layout)
                    {i > 0 && (
                      <button
                        type="button"
                        className="fp-x"
                        style={{ marginLeft: 10 }}
                        onClick={() => removeWard(i)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="fp-chips">
                    {sizes.map((sz) => (
                      <Chip
                        key={sz}
                        label={`${sz} ft · ${WSIZES[sz]}`}
                        on={w.ws === sz}
                        dis={sz > maxW}
                        onClick={() => setWs(i, sz)}
                      />
                    ))}
                  </div>
                  {w.ws > 0 && (
                    <>
                      <div className="fp-gt">
                        Wardrobe{room.wards.length > 1 ? ` ${i + 1}` : ""} type
                      </div>
                      <div className="fp-chips">
                        {WTYPES.map((t) => {
                          const dis =
                            (t.startsWith("Openable") && w.ws > maxWOpen) ||
                            (t.startsWith("Walk-in") &&
                              (area < 160 || shortSide < 10));
                          return (
                            <Chip
                              key={t}
                              label={t}
                              on={w.wt === t}
                              dis={dis}
                              onClick={() => setWt(i, t)}
                            />
                          );
                        })}
                      </div>
                      <div className="fp-note">
                        Openable doors need extra floor space to swing; walk-in
                        needs a larger room (about 160 sq ft+). Unavailable
                        types are crossed out.
                      </div>
                      <div className="fp-gt">
                        Wardrobe{room.wards.length > 1 ? ` ${i + 1}` : ""}{" "}
                        height
                      </div>
                      <div className="fp-chips">
                        {WHEIGHTS.map((v) => (
                          <Chip
                            key={v}
                            label={v}
                            on={w.wh === v}
                            onClick={() => setWh(i, v)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {room.wards.length < 3 &&
                room.wards[room.wards.length - 1].ws > 0 && (
                  <button
                    type="button"
                    className="fp-btn alt"
                    style={{ marginTop: 12 }}
                    onClick={addWard}
                  >
                    + Add another wardrobe
                  </button>
                )}
            </>
          )}
        </>
      )}

      {cfg.ex.length > 0 && (
        <>
          <div className="fp-gt">Add more</div>
          <div className="fp-chips">
            {cfg.ex.map((v) => (
              <Chip
                key={v}
                label={plannerOptionLabel(v)}
                on={room.ex.includes(v)}
                onClick={() => toggleEx(v)}
              />
            ))}
          </div>
        </>
      )}

      <div className="fp-gt">Civil work</div>
      <div className="fp-chips">
        {(CW_LIST[room.t] || []).map((v) => (
          <Chip
            key={v}
            label={plannerOptionLabel(v)}
            on={room.cw.includes(v)}
            onClick={() => toggleCw(v)}
          />
        ))}
      </div>
      <div className="fp-note">
        Civil work is priced on this room's size (floor area, wall area or
        running ft). Exact quantities are confirmed after a site visit. Not
        sure? Skip it — our team will advise.
      </div>

      {warnings.map((w, i) => (
        <div key={i} className="fp-note warn">
          ⚠ {w}
        </div>
      ))}

      <div className="fp-rtot">
        <span>{cfg.n} estimate</span>
        <span>{inr(priced.total)}</span>
      </div>
    </div>
  );
}

/* Clear wardrobe choices that no longer fit after a bed/room-size change */
function refit(room) {
  if (!room.wards || !room.wards.length) return room;
  const m = maxWardrobe(room, 2.5);
  const mo = maxWardrobe(room, 3.5);
  const wards = room.wards.map((w) => {
    if (!w.ws) return w;
    if (m === null || w.ws > m) return { ws: 0, wt: "", wh: "" };
    if (w.wt.startsWith("Openable") && w.ws > mo) return { ...w, wt: "" };
    return w;
  });
  return { ...room, wards };
}

/* ===== Main component ===== */
export default function FurniturePlanner() {
  const [rooms, setRooms] = useState(() =>
    ["master", "child", "kitchen", "living", "entrance"].map(newRoom),
  );
  const [contact, setContact] = useState({
    name: "",
    phone: "",
    email: "",
    property: "3 BHK",
    floor: "Ground Floor",
    location: "",
    notes: "",
  });
  const [okMsg, setOkMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const [roomIdx, setRoomIdx] = useState(0);
  const [pkg, setPkg] = useState("premium"); // basic | premium | luxury (Premium = the saved Rate Master rates)
  const [catalogueProducts, setCatalogueProducts] = useState([]);
  const [catalogueVersion, setCatalogueVersion] = useState(0);

  /* Fetch latest rates from the backend (admin Rate Master) and apply them.
     Called on page load, when the customer reaches the Estimate step, and when the tab/window regains focus.
     cache: "no-store" makes sure the browser never reuses an old response. */
  const lastRatesFetch = useRef(0);
  const loadRates = useCallback(() => {
    lastRatesFetch.current = Date.now();
    return Promise.all([
      fetch(`${API_BASE_URL}/api/products?active=true`, {
        cache: "no-store",
      }).then((response) => (response.ok ? response.json() : null)),
      fetch(`${API_BASE_URL}/api/planner/config`, { cache: "no-store" })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null),
    ])
      .then(([payload, configPayload]) => {
        const products = Array.isArray(payload?.data) ? payload.data : [];
        applyPlannerRates(products);
        products.forEach((product) => {
          const price = Number(product.price);
          if (product.category === "Bed") {
            const type = catalogueOption(product, "Bed type");
            if (type && Number.isFinite(price)) {
              BED_CATALOGUE[type] = {
                price,
                unit: String(product.unit || "").toUpperCase(),
              };
            }
          }
          if (product.category === "Sofa") {
            const key = catalogueOption(product, "Sofa");
            if (P[`Sofa|${key}`] !== undefined) P[`Sofa|${key}`] = price;
          }
          if (
            product.category === "Kitchen" &&
            KITCHEN_RATE[product.material] !== undefined
          )
            KITCHEN_RATE[product.material] = price;
          if (
            product.category === "Wardrobe" &&
            WARDROBE_RATE[product.material] !== undefined
          )
            WARDROBE_RATE[product.material] = price;
          if (
            product.category === "Bed" &&
            BED_RATE[product.material] !== undefined
          )
            BED_RATE[product.material] = price;
        });
        // The rate master is the final source of truth. Product catalogue values
        // remain only as a fallback when the rate-master migration is not imported.
        applyPlannerConfig(configPayload?.data);
        setCatalogueProducts(products);
        setCatalogueVersion((value) => value + 1);
      })
      .catch(() => {});
  }, []);

  /* 1) On page load */
  useEffect(() => {
    loadRates();
  }, [loadRates]);

  /* 2) When the customer comes back to this tab / window (throttled to once per 5 seconds) */
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastRatesFetch.current < 5000) return;
      loadRates();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadRates]);

  const quoteRef = useRef(
    (() => {
      const d = new Date();
      const y = d.getFullYear();
      const a = d.getMonth() >= 3 ? y : y - 1;
      return `DH/${String(Date.now()).slice(-5)}/${a}-${String(a + 1).slice(2)}`;
    })(),
  );
  const uploadCache = useRef({ key: "", result: null });

  const updateRoom = (id, next) =>
    setRooms((rs) => rs.map((r) => (r.id === id ? next : r)));
  const removeRoom = (id) => setRooms((rs) => rs.filter((r) => r.id !== id));
  const addRoomOfType = (t) => setRooms((rs) => [...rs, newRoom(t)]);
  const removeLastOfType = (t) =>
    setRooms((rs) => {
      const idx = rs.map((r) => r.t).lastIndexOf(t);
      return idx === -1 ? rs : rs.filter((_, i) => i !== idx);
    });
  const countOf = (t) => rooms.filter((r) => r.t === t).length;

  const priced = useMemo(
    () => rooms.map((r) => ({ room: r, ...priceRoom(r, pkg) })),
    [rooms, catalogueVersion, pkg],
  );
  const interior = priced
    .filter((x) => !TYPES[x.room.t].elec)
    .reduce((s, x) => s + x.total, 0);
  const electrical = priced
    .filter((x) => TYPES[x.room.t].elec)
    .reduce((s, x) => s + x.total, 0);
  const subtotal = interior + electrical;
  const installation = Math.round(subtotal * PLANNER_SETTINGS.installationPct);
  const totalCost = Math.round(subtotal + installation);
  const discount =
    PLANNER_SETTINGS.discountRound &&
    totalCost >= PLANNER_SETTINGS.discountRound
      ? totalCost % PLANNER_SETTINGS.discountRound
      : 0;
  const gst = (totalCost - discount) * PLANNER_SETTINGS.gst;
  const grandTotal = totalCost - discount + gst;

  /* ===== Step navigation ===== */
  const cur = Math.min(roomIdx, Math.max(rooms.length - 1, 0));
  const currentRoom = rooms[cur];

  const go = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    if (step === 1 && rooms.length === 0) {
      alert("Please add at least one room to continue.");
      return;
    }
    if (step === 1) setRoomIdx(0);
    if (step === 3 && cur < rooms.length - 1) {
      setRoomIdx(cur + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (step === 4) {
      if (!contact.name || !contact.phone) {
        alert("Please enter your name and phone number.");
        return;
      }
      if (!isEmail(contact.email)) {
        alert(
          "Please enter a valid email – the quotation PDF will be mailed to you.",
        );
        return;
      }
      loadRates(); // reaching the estimate step – always pick up the latest admin rates
    }
    go(step + 1);
  };
  const goBack = () => {
    if (step === 3 && cur > 0) {
      setRoomIdx(cur - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (step === 4) setRoomIdx(Math.max(rooms.length - 1, 0));
    go(step - 1);
  };
  const jumpTo = (n) => {
    if (n < step) go(n);
  };

  /* ===== Text summary (used for email body + WhatsApp) ===== */
  function summaryText() {
    let s = `Dwellora Designs – Requirement\nName: ${contact.name}\nPhone: ${contact.phone}\nEmail: ${contact.email}\nProperty: ${contact.property}\nFloor: ${contact.floor}\nSite location: ${contact.location || "-"}\nPackage: ${PACKAGES[pkg].name}\n`;
    priced.forEach((x) => {
      s += `\n${roomTitle(x.room)} – ${inr(x.total)}\n`;
      s += x.items.length
        ? x.items
            .map(
              (i) =>
                ` - ${i.l}${i.d && i.r ? ` [${i.d} x ${inr(i.r)}/${UNIT_TXT[i.u] || "unit"}]` : ""}${i.a ? ": " + inr(i.a) : ""}`,
            )
            .join("\n") + "\n"
        : " - (no selections yet)\n";
    });
    s += `\nInterior turnkey project cost: ${inr(interior)}\nElectrical work: ${inr(electrical)}\nTotal: ${inr(subtotal)}\nArchitectural consultancy fees (${PLANNER_SETTINGS.installationPct * 100}%): ${inr(installation)}\nTotal cost: ${inr(totalCost)}\nDiscount: ${inr(discount)}\nGST (${plannerGstLabel()}): ${inr(gst)}\nESTIMATED TOTAL: ${inr(grandTotal)}\nIn words: ${amountInWords(grandTotal)}`;
    if (contact.notes) s += `\n\nNotes: ${contact.notes}`;
    return s;
  }

  function whatsappText(pdfUrl) {
    let s = `*Dwellora Homes – Interior Quotation*\n\n`;
    s += `*Name:* ${contact.name}\n*Mobile:* ${contact.phone}\n*Email:* ${contact.email}\n*Property:* ${contact.property}\n*Floor:* ${contact.floor}\n*Site location:* ${contact.location || "-"}\n*Package:* ${PACKAGES[pkg].name}\n`;
    s += `\n*Details:*\n`;
    priced.forEach((x, i) => {
      s += `${i + 1}. ${roomTitle(x.room)} – ${inr(x.total)}\n`;
    });
    s += `\nTotal: ${inr(subtotal)}\nArchitectural consultancy fees (${PLANNER_SETTINGS.installationPct * 100}%): ${inr(installation)}\nTotal cost: ${inr(totalCost)}\nDiscount: ${inr(discount)}\nGST (${plannerGstLabel()}): ${inr(gst)}\n*Grand Total: ${inr(grandTotal)}*\n${amountInWords(grandTotal)}\n`;
    if (contact.notes) s += `\nNotes: ${contact.notes}\n`;
    s += `\n*Quotation PDF:*\n${pdfUrl}`;
    return s;
  }

  const makePdf = () =>
    buildQuotationPdf({
      contact,
      priced,
      interior,
      electrical,
      subtotal,
      installation,
      totalCost,
      discount,
      gst,
      grandTotal,
      ref: quoteRef.current,
      pkgInfo: PACKAGES[pkg],
    });

  /* ===== Upload PDF + data to backend (saves PDF, emails owner & client) =====
     Result is cached, so WhatsApp + "Send" buttons don't send duplicate emails
     unless the quotation was changed (including a rate refresh or a package change). */
  async function uploadQuotation() {
    if (!contact.name || !contact.phone)
      throw new Error("Please enter your name and phone number.");
    if (!isEmail(contact.email))
      throw new Error("Please enter a valid email address.");

    const key = JSON.stringify({ contact, rooms, catalogueVersion, pkg });
    if (uploadCache.current.key === key && uploadCache.current.result)
      return uploadCache.current.result;

    const { doc, fileName } = makePdf();
    const payload = {
      ...contact,
      ref: quoteRef.current,
      rooms: priced.map((x) => ({
        room: TYPES[x.room.t].n,
        size: TYPES[x.room.t].elec ? "-" : `${x.room.L}x${x.room.W}`,
        items: x.items,
        total: Math.round(x.total),
      })),
      interior: Math.round(interior),
      electrical: Math.round(electrical),
      subtotal: Math.round(subtotal),
      installation: Math.round(installation),
      totalCost: Math.round(totalCost),
      discount: Math.round(discount),
      gst: Math.round(gst),
      grandTotal: Math.round(grandTotal),
      amountInWords: amountInWords(grandTotal),
      text: summaryText(),
    };
    const fd = new FormData();
    fd.append("data", JSON.stringify(payload));
    fd.append("pdf", doc.output("blob"), fileName);

    const response = await fetch(SUBMIT_URL, { method: "POST", body: fd });
    const result = await response.json();

    if (!response.ok || !result.success)
      throw new Error(result.message || "Unable to submit the enquiry.");
    uploadCache.current = { key, result };
    return result;
  }

  /* ===== Buttons ===== */
  const submitEnquiry = async () => {
    setBusy(true);
    setOkMsg("");
    try {
      await uploadQuotation();
      setOkMsg(
        "Thank you! The quotation PDF has been emailed to you and to our design team. We will contact you shortly.",
      );
    } catch (e) {
      alert(e.message || "Could not send. Please try WhatsApp instead.");
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsApp = async () => {
    // open the tab immediately (inside the click) so the browser doesn't block it after the async upload
    const win = window.open("", "_blank");
    setBusy(true);
    setOkMsg("");
    try {
      const result = await uploadQuotation();
      const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(whatsappText(result.pdfUrl))}`;
      if (win) win.location.href = url;
      else window.location.href = url;
      setOkMsg(
        "Quotation PDF created and emailed to you and our team. Tap Send in WhatsApp to share it.",
      );
    } catch (e) {
      if (win) win.close();
      alert(e.message || "Could not create the quotation. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    const { doc, fileName } = makePdf();
    doc.save(fileName);
  };

  /* Print = the SAME PDF, opened with the print dialog (which also has Save as PDF) */
  const printPdf = () => {
    const { doc } = makePdf();
    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  };

  const copySummary = async () => {
    await navigator.clipboard.writeText(summaryText());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const nextLabel =
    step === 3 && cur < rooms.length - 1
      ? "Next room →"
      : step === 4
        ? "Continue →"
        : "Next →";

  return (
    <div className="fp-wrap">
      <style>{CSS}</style>
      <h1>Plan Your Interiors</h1>
      <p className="fp-sub">
        Follow the steps below. Pick your furniture room by room and we'll check
        what fits and calculate an estimate.
      </p>

      {/* ===== Stepper ===== */}
      <div className="fp-steps">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const state = n === step ? " active" : n < step ? " done" : "";
          return (
            <button
              key={label}
              type="button"
              className={`fp-step${state}`}
              onClick={() => jumpTo(n)}
              disabled={n >= step}
            >
              <span className="fp-step-n">{n < step ? "✓" : n}</span>
              <span className="fp-step-l">{label}</span>
            </button>
          );
        })}
      </div>

      {/* ===== Step 1: Rooms ===== */}
      {step === 1 && (
        <div className="fp-card">
          <h2>Step 1 · Which rooms do you need?</h2>
          <p className="fp-sub" style={{ marginBottom: 12 }}>
            Tap a room to add it. Tap again to add another of the same kind (for
            example, a second bedroom). You will set the size and furniture of
            each room in the next steps. Add "Electrical Work" for wiring, light
            points and switches.
          </p>

          <div className="fp-tabs">
            {Object.entries(TYPES).map(([k, v]) => {
              const c = countOf(k);
              return (
                <div key={k} className={`fp-tab${c > 0 ? " on" : ""}`}>
                  <button
                    type="button"
                    className="fp-tab-main"
                    onClick={() => addRoomOfType(k)}
                  >
                    <span className="fp-tab-name">{v.n}</span>
                    <span className="fp-tab-hint">
                      {c > 0 ? `${c} added` : "+ Add"}
                    </span>
                  </button>
                  {c > 0 && (
                    <>
                      <button
                        type="button"
                        className="fp-tab-minus"
                        aria-label={`Remove one ${v.n}`}
                        onClick={() => removeLastOfType(k)}
                      >
                        −
                      </button>
                      <button
                        type="button"
                        className="fp-tab-plus"
                        aria-label={`Add one more ${v.n}`}
                        onClick={() => addRoomOfType(k)}
                      >
                        +
                      </button>
                    </>
                  )}
                  {c > 0 && <span className="fp-tab-badge">{c}</span>}
                </div>
              );
            })}
          </div>

          <div className="fp-gt" style={{ marginTop: 18 }}>
            Your rooms ({rooms.length})
          </div>
          <div className="fp-roomlist">
            {rooms.length === 0 && (
              <div className="fp-note">
                No rooms yet. Tap a room above to add it.
              </div>
            )}
            {rooms.map((r, i) => (
              <div key={r.id} className="fp-roomrow">
                <span>
                  {i + 1}. {TYPES[r.t].n}
                </span>
                <button
                  type="button"
                  className="fp-x"
                  onClick={() => removeRoom(r.id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Step 2: Package ===== */}
      {step === 2 && (
        <div className="fp-card">
          <h2>Step 2 · Choose your package</h2>
          <p className="fp-sub" style={{ marginBottom: 12 }}>
            The package sets the quality of materials used for your furniture.
            Your estimate and quotation follow the package you select, and its
            full material specifications are printed in the quotation PDF.
          </p>

          <div className="fp-pkgs">
            {/* Basic */}
            <div className={`fp-pkg${pkg === "basic" ? " on" : ""}`}>
              <span className="fp-pkg-badge">Basic</span>
              <div>
                <div className="fp-pkg-price">
                  {inr(pkgSqftRate("basic"))} <small>/ sq ft</small>
                </div>
                <div className="fp-pkg-sub">Sliding wardrobe, starting rate</div>
              </div>
              <ul className="fp-pkg-list">
                {PACKAGES.basic.specs.slice(0, 5).map(([category, spec]) => (
                  <li key={category}>
                    <strong>{category}</strong>
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={`fp-btn${pkg === "basic" ? "" : " alt"}`}
                onClick={() => setPkg("basic")}
              >
                {pkg === "basic" ? "Basic selected ✓" : "Select Basic"}
              </button>
            </div>

            {/* Premium */}
            <div className={`fp-pkg${pkg === "premium" ? " on" : ""}`}>
              <span className="fp-pkg-badge">Premium</span>
              <div>
                <div className="fp-pkg-price">
                  {inr(pkgSqftRate("premium"))} <small>/ sq ft</small>
                </div>
                <div className="fp-pkg-sub">Sliding wardrobe, starting rate</div>
              </div>
              <ul className="fp-pkg-list">
                {PACKAGES.premium.specs.slice(0, 5).map(([category, spec]) => (
                  <li key={category}>
                    <strong>{category}</strong>
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={`fp-btn${pkg === "premium" ? "" : " alt"}`}
                onClick={() => setPkg("premium")}
              >
                {pkg === "premium" ? "Premium selected ✓" : "Select Premium"}
              </button>
            </div>

            {/* Luxury */}
            <div className={`fp-pkg${pkg === "luxury" ? " on" : ""}`}>
              <span className="fp-pkg-badge">Luxury</span>
              <div>
                <div className="fp-pkg-price">
                  {inr(pkgSqftRate("luxury"))} <small>/ sq ft</small>
                </div>
                <div className="fp-pkg-sub">Sliding wardrobe, starting rate</div>
              </div>
              <ul className="fp-pkg-list">
                {PACKAGES.luxury.specs.slice(0, 5).map(([category, spec]) => (
                  <li key={category}>
                    <strong>{category}</strong>
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={`fp-btn${pkg === "luxury" ? "" : " alt"}`}
                onClick={() => setPkg("luxury")}
              >
                {pkg === "luxury" ? "Luxury selected ✓" : "Select Luxury"}
              </button>
            </div>
          </div>

          <div className="fp-note">
            Civil work and electrical work are priced separately and do not
            change with the package.
          </div>
        </div>
      )}

      {/* ===== Step 3: Furniture ===== */}
      {step === 3 && (
        <>
          <div className="fp-card">
            <h2>Step 3 · Furniture for each room</h2>
            <p className="fp-sub" style={{ marginBottom: 12 }}>
              Room {rooms.length ? cur + 1 : 0} of {rooms.length}. Enter the
              room size in feet. Options update to what fits, and the price
              updates as you choose.
            </p>
            <div className="fp-chips">
              {rooms.map((r, i) => (
                <Chip
                  key={r.id}
                  label={`${i + 1}. ${TYPES[r.t].n}`}
                  on={i === cur}
                  onClick={() => {
                    setRoomIdx(i);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              ))}
            </div>
          </div>

          {currentRoom ? (
            <RoomCard
              key={currentRoom.id}
              room={currentRoom}
              onChange={(next) => updateRoom(currentRoom.id, next)}
              onRemove={() => removeRoom(currentRoom.id)}
              products={catalogueProducts}
              pkg={pkg}
            />
          ) : (
            <div className="fp-card">
              <p className="fp-sub">
                You have no rooms left. Go back and add a room.
              </p>
            </div>
          )}

          <div className="fp-running">
            <span>
              Estimate so far, {PACKAGES[pkg].name} package (incl. consultancy
              fees{PLANNER_SETTINGS.gst ? " & GST" : ""})
            </span>
            <strong>{inr(grandTotal)}</strong>
          </div>
        </>
      )}

      {/* ===== Step 4: Your details ===== */}
      {step === 4 && (
        <div className="fp-card">
          <h2>Step 4 · Your details</h2>
          <label>Name</label>
          <input
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            placeholder="Full name"
          />
          <div className="fp-row">
            <div>
              <label>Phone</label>
              <input
                value={contact.phone}
                onChange={(e) =>
                  setContact({ ...contact, phone: e.target.value })
                }
                placeholder="Mobile number"
              />
            </div>
            <div>
              <label>Property</label>
              <select
                value={contact.property}
                onChange={(e) =>
                  setContact({ ...contact, property: e.target.value })
                }
              >
                {["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Villa"].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="fp-row">
            <div>
              <label>Floor</label>
              <select
                value={contact.floor}
                onChange={(e) =>
                  setContact({ ...contact, floor: e.target.value })
                }
              >
                {[
                  "Ground Floor",
                  "First Floor",
                  "Second Floor",
                  "Third Floor",
                  "Duplex",
                  "Full Building",
                ].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Site location</label>
              <input
                value={contact.location}
                onChange={(e) =>
                  setContact({ ...contact, location: e.target.value })
                }
                placeholder="e.g. Wakad, Pune"
              />
            </div>
          </div>
          <label>Email (your quotation PDF will be mailed here)</label>
          <input
            type="email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            placeholder="you@example.com"
          />
          <label>Notes (optional)</label>
          <textarea
            rows={2}
            value={contact.notes}
            onChange={(e) => setContact({ ...contact, notes: e.target.value })}
            placeholder="Style, budget, timeline..."
          />
        </div>
      )}

      {/* ===== Step 5: Estimate ===== */}
      {step === 5 && (
        <div className="fp-card fp-sum">
          <h2>Step 5 · Your estimate</h2>
          {rooms.length === 0 ? (
            <p className="fp-sub">Add a room to begin.</p>
          ) : (
            <>
              <h4>
                <span>Package</span>
                <span>{PACKAGES[pkg].name}</span>
              </h4>
              {priced.map((x) => (
                <div key={x.room.id}>
                  <h4>
                    <span>{roomTitle(x.room)}</span>
                    <span>{inr(x.total)}</span>
                  </h4>
                  {x.items.length ? (
                    x.items.map((i, idx) => (
                      <div key={idx} className="fp-li">
                        <span>
                          {i.l}
                          {i.d && i.r ? (
                            <em className="fp-qty">
                              {" "}
                              — {i.d} × {inr(i.r)}/{UNIT_TXT[i.u] || "unit"}
                            </em>
                          ) : null}
                        </span>
                        <span>{i.a ? inr(i.a) : "—"}</span>
                      </div>
                    ))
                  ) : (
                    <div className="fp-li">
                      <span>No selections yet</span>
                    </div>
                  )}
                </div>
              ))}
              <div className="fp-tot">
                <div className="fp-li">
                  <span>Interior turnkey project cost</span>
                  <span>{inr(interior)}</span>
                </div>
                <div className="fp-li">
                  <span>Electrical work (approx., incl. fittings)</span>
                  <span>{inr(electrical)}</span>
                </div>
                <div className="fp-li">
                  <span>Total</span>
                  <span>{inr(subtotal)}</span>
                </div>
                <div className="fp-li">
                  <span>
                    Architectural consultancy fees (
                    {PLANNER_SETTINGS.installationPct * 100}%)
                  </span>
                  <span>{inr(installation)}</span>
                </div>
                <div className="fp-li">
                  <span>Total cost</span>
                  <span>{inr(totalCost)}</span>
                </div>
                <div className="fp-li">
                  <span>Discount</span>
                  <span>{inr(discount)}</span>
                </div>
                <div className="fp-li">
                  <span>GST ({plannerGstLabel()})</span>
                  <span>{inr(gst)}</span>
                </div>
                <div className="fp-grand">
                  <span>Grand total</span>
                  <span>{inr(grandTotal)}</span>
                </div>
                <div className="fp-fine">
                  <strong>In words:</strong> {amountInWords(grandTotal)}
                </div>
                <div className="fp-fine">
                  This is an indicative estimate (±10%). Final price is
                  confirmed after our designer reviews your requirement and site
                  measurements.
                </div>
              </div>
            </>
          )}
          <div className="fp-actions">
            <button
              type="button"
              className="fp-btn"
              disabled={busy}
              onClick={submitEnquiry}
            >
              {busy ? "Please wait…" : "Send to Dwellora Designs"}
            </button>
            <button
              type="button"
              className="fp-btn alt"
              disabled={busy}
              onClick={shareWhatsApp}
            >
              Share on WhatsApp
            </button>
            <button type="button" className="fp-btn alt" onClick={downloadPdf}>
              Download PDF
            </button>
            <button type="button" className="fp-btn alt" onClick={printPdf}>
              Print
            </button>
            <button type="button" className="fp-btn alt" onClick={copySummary}>
              {copied ? "Copied ✓" : "Copy summary"}
            </button>
          </div>
          {okMsg && <div className="fp-ok-msg">{okMsg}</div>}
        </div>
      )}

      {/* ===== Back / Next ===== */}
      <div className="fp-nav">
        {step > 1 ? (
          <button type="button" className="fp-btn alt" onClick={goBack}>
            ← Back
          </button>
        ) : (
          <span />
        )}
        {step < 5 && (
          <button type="button" className="fp-btn" onClick={goNext}>
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ===== Scoped CSS ===== */
const CSS = `
.fp-wrap{max-width:1060px;margin:0 auto;padding:120px 20px 60px;font:15px/1.5 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#2a2622;}
.fp-wrap h1{font-size:26px;margin:0 0 4px}
.fp-wrap h2{font-size:18px;margin:0 0 12px}
.fp-sub{color:#6f675e;margin:0 0 20px}
.fp-steps{display:flex;gap:8px;margin-bottom:20px;overflow-x:auto;padding-bottom:4px}
.fp-step{flex:1;min-width:0;display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #e6dfd4;border-radius:12px;padding:8px 10px;font:inherit;font-size:13px;color:#6f675e;cursor:default;text-align:left}
.fp-step-n{flex:none;width:24px;height:24px;border-radius:50%;background:#f3ece1;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px}
.fp-step-l{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fp-step.active{border-color:#b5651d;color:#2a2622;font-weight:600}
.fp-step.active .fp-step-n{background:#b5651d;color:#fff}
.fp-step.done{cursor:pointer;color:#2f7d4f}
.fp-step.done .fp-step-n{background:#2f7d4f;color:#fff}
@media(max-width:640px){.fp-wrap{padding:100px 14px 50px}.fp-step-l{display:none}.fp-step{justify-content:center}}
.fp-card{background:#fff;border:1px solid #e6dfd4;border-radius:14px;padding:16px;margin-bottom:16px}
.fp-wrap label{display:block;font-size:13px;color:#6f675e;margin:10px 0 4px}
.fp-wrap input,.fp-wrap select,.fp-wrap textarea{width:100%;padding:9px 10px;border:1px solid #e6dfd4;border-radius:8px;background:#fff;color:#2a2622;font:inherit}
.fp-row{display:flex;gap:10px}
.fp-row>div{flex:1}
.fp-chips{display:flex;flex-wrap:wrap;gap:8px}
.fp-chip{border:1px solid #e6dfd4;background:#fff;color:#2a2622;padding:7px 12px;border-radius:999px;cursor:pointer;font:inherit;font-size:13.5px}
.fp-chip:hover{border-color:#b5651d}
.fp-chip.on{background:#b5651d;color:#fff;border-color:#b5651d}
.fp-chip.dis{opacity:.4;cursor:not-allowed;text-decoration:line-through}
.fp-gt{font-size:13px;font-weight:600;margin:14px 0 6px}
.fp-room-h{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:6px}
.fp-room-h h3{margin:0;font-size:17px}
.fp-dims{display:flex;align-items:center;gap:6px;font-size:13px;color:#6f675e}
.fp-dims input{width:64px;padding:6px 8px}
.fp-x{background:none;border:none;color:#b3541e;cursor:pointer;font-size:13px}
.fp-note{font-size:13px;padding:8px 10px;border-radius:8px;margin-top:10px;background:#f3ece1}
.fp-note.ok{color:#2f7d4f}
.fp-note.warn{color:#b3541e}
.fp-rtot{display:flex;justify-content:space-between;margin-top:14px;padding-top:10px;border-top:1px dashed #e6dfd4;font-weight:600}
.fp-btn{background:#b5651d;color:#fff;border:none;padding:10px 18px;border-radius:10px;font:inherit;font-weight:600;cursor:pointer}
.fp-btn.alt{background:transparent;color:#2a2622;border:1px solid #e6dfd4}
.fp-btn:disabled{opacity:.6;cursor:wait}
.fp-tabs{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
.fp-tab{position:relative;display:block;border:1px solid #e6dfd4;border-radius:12px;background:#fff;transition:border-color .15s,background .15s}
.fp-tab:hover{border-color:#b5651d}
.fp-tab.on{border-color:#b5651d;background:#fbf3ea}
.fp-tab-main{width:100%;display:flex;flex-direction:column;align-items:flex-start;gap:2px;background:none;border:none;padding:10px 12px;font:inherit;color:#2a2622;text-align:left;cursor:pointer;border-radius:12px}
.fp-tab-name{font-weight:600;font-size:14px;line-height:1.25}
.fp-tab-hint{font-size:12.5px;color:#b5651d}
.fp-tab.on .fp-tab-hint{color:#2f7d4f;font-weight:600}
.fp-tab-minus,.fp-tab-plus{position:absolute;bottom:8px;width:24px;height:24px;padding:0;display:flex;align-items:center;justify-content:center;background:#fff;border:1px solid #e6dfd4;border-radius:50%;font-size:16px;line-height:1;cursor:pointer}
.fp-tab-plus{right:8px;color:#2f7d4f}
.fp-tab-minus{right:38px;color:#b3541e}
.fp-tab-minus:hover,.fp-tab-plus:hover{border-color:#b5651d;background:#f3ece1}
.fp-tab-badge{position:absolute;top:-8px;right:-6px;min-width:22px;height:22px;padding:0 6px;border-radius:999px;background:#b5651d;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;pointer-events:none}
.fp-tab-main:focus-visible,.fp-tab-minus:focus-visible,.fp-tab-plus:focus-visible,.fp-chip:focus-visible,.fp-btn:focus-visible{outline:2px solid #b5651d;outline-offset:2px}
.fp-roomlist{margin-top:6px}
.fp-roomrow{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border:1px solid #e6dfd4;border-radius:10px;margin-bottom:8px;background:#faf7f2}
.fp-running{display:flex;justify-content:space-between;align-items:center;gap:12px;background:#f3ece1;border-radius:12px;padding:12px 16px;margin-bottom:16px;font-size:14px}
.fp-running strong{color:#b5651d;font-size:18px}
.fp-nav{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:8px}
.fp-sum h4{margin:14px 0 4px;font-size:14px;display:flex;justify-content:space-between}
.fp-li{display:flex;justify-content:space-between;gap:12px;color:#6f675e;font-size:13.5px;padding:2px 0}
.fp-tot{margin-top:18px;background:#f3ece1;border-radius:12px;padding:14px}
.fp-tot .fp-li{color:#2a2622;font-size:14px}
.fp-grand{display:flex;justify-content:space-between;align-items:baseline;margin-top:8px;padding-top:10px;border-top:1px solid #e6dfd4;font-size:20px;font-weight:700;color:#b5651d}
.fp-fine{font-size:12px;color:#6f675e;margin-top:8px}
.fp-actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:14px}
.fp-ok-msg{color:#2f7d4f;margin-top:10px;font-weight:600}
.fp-qty{font-style:normal;color:#9a9086;font-size:12.5px}
.fp-el{display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px dashed #e6dfd4}
.fp-el-l{flex:1;font-size:13px}
.fp-wrap .fp-el input{width:90px;flex:none}
.fp-pkgs{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:14px;margin-top:6px}
.fp-pkg{display:flex;flex-direction:column;gap:12px;background:#fff;border:1px solid #e6dfd4;border-radius:14px;padding:16px;transition:border-color .15s,background .15s}
.fp-pkg:hover{border-color:#b5651d}
.fp-pkg.on{border:2px solid #b5651d;background:#fbf3ea;padding:15px}
.fp-pkg-badge{align-self:flex-start;background:#b5651d;color:#fff;font-size:13px;font-weight:700;padding:4px 14px;border-radius:999px}
.fp-pkg-price{font-size:26px;font-weight:700;line-height:1.15;color:#2a2622}
.fp-pkg-price small{font-size:13px;font-weight:500;color:#6f675e}
.fp-pkg-sub{font-size:12.5px;color:#6f675e;margin-top:2px}
.fp-pkg-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column}
.fp-pkg-list li{display:flex;flex-direction:column;gap:2px;padding:8px 0;border-top:1px solid #efe8dc;font-size:13px}
.fp-pkg-list li strong{font-size:12.5px;font-weight:600;color:#b5651d}
.fp-pkg-list li span{color:#2a2622}
.fp-pkg .fp-btn{margin-top:auto}
.fp-product-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(145px,1fr));gap:10px;margin:8px 0}.fp-product-choice{background:#fff;border:1px solid #e6dfd4;border-radius:10px;color:#2a2622;display:grid;gap:5px;overflow:hidden;padding:0 0 10px;text-align:left}.fp-product-choice.is-selected{border:2px solid #b5651d}.fp-product-choice img,.fp-product-placeholder{background:#f3ece1;height:92px;object-fit:cover;width:100%}.fp-product-placeholder{align-items:center;color:#8c8175;display:flex;font-size:12px;justify-content:center}.fp-product-choice strong,.fp-product-choice small,.fp-product-choice em{padding:0 10px}.fp-product-choice strong{font-size:13px}.fp-product-choice small{color:#6f675e;font-size:12px}.fp-product-choice em{color:#b5651d;font-size:12px;font-style:normal;font-weight:bold}
`;